/* eslint-disable react/jsx-no-target-blank, jsx-a11y/anchor-is-valid */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import './Home1Page.css';
import Home1PositionChart from '../components/charts/Home1PositionChart';
import useOverviewData from '../hooks/useOverviewData';
import { useBonusesData } from '../hooks/useBonusesData';
import { loadExternalScript } from '../utils/scriptLoader';
import { DATA_YEARS, SIDEBETS_CSV_BY_YEAR, SOCIAL_CSV_BY_YEAR, getCsvUrls } from '../utils/dataSources';

const CURRENT_YEAR = new Date().getFullYear();
const IMAGE_BASE = process.env.PUBLIC_URL || '';
const HOME1_D3_URL = 'https://d3js.org/d3.v7.min.js';
const VEGAS_MATT_SITE_URL = 'https://www.vegasmatt.com';
const VEGAS_MATT_SUBSCRIBE_URL = 'https://manage.kmail-lists.com/subscriptions/subscribe?a=WFHDhL&g=W8DZFM';
const HOME1_SIDEBETS_CSVS = getCsvUrls(SIDEBETS_CSV_BY_YEAR);
const HOME1_SOCIAL_CSVS = getCsvUrls(SOCIAL_CSV_BY_YEAR);
const DEFAULT_POSITION_YEAR = DATA_YEARS.includes(CURRENT_YEAR)
  ? CURRENT_YEAR
  : DATA_YEARS[DATA_YEARS.length - 1] ?? CURRENT_YEAR;
const HOME1_SITE_NAV_LINKS = [
  { id: 'Cruises', label: 'Cruises', href: 'https://www.vegasmatt.com/cruises' },
  { id: 'Events', label: 'Events', href: 'https://www.vegasmatt.com/events' },
  { id: 'Venues', label: 'Venues', href: 'https://www.vegasmatt.com/venues' },
  { id: 'About', label: 'About', href: 'https://www.vegasmatt.com/about' },
  { id: 'Socials', label: 'Socials', href: 'https://www.vegasmatt.com/links' },
  {
    id: 'Subscribe',
    label: 'Subscribe',
    href: 'https://manage.kmail-lists.com/subscriptions/subscribe?a=WFHDhL&g=W8DZFM',
  },
  { id: 'Contact', label: 'Contact', href: 'https://www.vegasmatt.com/contact' },
];
const HOME1_RANGE_FILTERS = [
  ...[...DATA_YEARS].sort((a, b) => b - a).map((year) => ({ key: `${year}`, label: `${year}` })),
  { key: 'all', label: 'All time' },
];
const HOME1_FOOTER_COLUMNS = [
  {
    title: 'Join us',
    links: [
      { id: 'Footer-Events-Link', label: 'Events', href: `${VEGAS_MATT_SITE_URL}/events`, current: true },
      { id: 'Footer-Cruises-Link', label: 'Cruises', href: `${VEGAS_MATT_SITE_URL}/cruises` },
      { id: 'Footer-Venues-Link', label: 'Venues', href: `${VEGAS_MATT_SITE_URL}/venues` },
      { id: 'Footer-Merch-Link', label: 'Merch', href: 'https://vegasmatt.shop', newTab: true },
      { id: 'Footer-Recipes-Link', label: 'Recipes', href: `${VEGAS_MATT_SITE_URL}/#` },
      { id: 'Footer-Partners-Link', label: 'Partners', href: `${VEGAS_MATT_SITE_URL}/partners` },
      { id: 'Footer-Subscribe-Link', label: 'Subscribe', href: VEGAS_MATT_SUBSCRIBE_URL, newTab: true },
      { id: 'Footer-Fanmail-Link', label: 'Fanmail', href: 'https://maps.app.goo.gl/Vqe196f3BEXhj1oeA' },
    ],
  },
  {
    title: 'Company',
    links: [
      { id: 'Footer-About-Link', label: 'About', href: `${VEGAS_MATT_SITE_URL}/about` },
      { id: 'Footer-News-Link', label: 'News', href: `${VEGAS_MATT_SITE_URL}/news` },
      { id: 'Footer-Contact-Link', label: 'Contact', href: `${VEGAS_MATT_SITE_URL}/contact` },
      { id: 'Footer-Links-Link', label: 'Links', href: `${VEGAS_MATT_SITE_URL}/links` },
      { id: 'Footer-Info-Email-Link', label: 'Info@', href: 'mailto:info@vegasmatt.com' },
      { id: 'Footer-Media-Email-Link', label: 'Media@', href: 'mailto:media@vegasmatt.com' },
      { id: 'Footer-Partners-Email-Link', label: 'Partners@', href: 'mailto:partners@vegasmatt.com' },
    ],
  },
  {
    title: 'Social',
    dynamic: true,
    links: [
      { id: 'Footer-YouTube-Link', label: 'YouTube', href: 'https://www.youtube.com/@VegasMatt', newTab: true },
      { id: 'Footer-Instagram-Link', label: 'Instagram', href: 'https://www.instagram.com/vegasmatt/', newTab: true },
      { id: 'Footer-TikTok-Link', label: 'TikTok', href: 'https://www.tiktok.com/@vegasmatt', newTab: true },
      { id: 'Footer-Facebook-Link', label: 'Facebook', href: 'https://www.facebook.com/itsvegasmatt/', newTab: true },
      { id: 'Footer-Discord-Link', label: 'Discord', href: 'https://discord.gg/vegasmatt', newTab: true },
      { id: 'Footer-Twitch-Link', label: 'Twitch', href: 'https://www.twitch.tv/thevegasmatt', newTab: true },
      { id: 'Footer-X-Link', label: 'X', href: 'https://x.com/itsvegasmatt', newTab: true },
      { id: 'Footer-Snapchat-Link', label: 'Snapchat', href: 'https://www.snapchat.com/add/itsvegasmatt', newTab: true },
      { id: 'Footer-Cameo-Link', label: 'Cameo', href: 'https://www.cameo.com/vegasmatt', newTab: true },
    ],
  },
  {
    title: 'FInd Us',
    dynamic: true,
    links: [
      { id: 'Footer-NV-Link', label: '', href: `${VEGAS_MATT_SITE_URL}/venues/day-of-gratitude-las-vegas-nv`, empty: true },
      { id: 'Footer-Agua-Caliente-CA-Link', label: 'Agua Caliente', href: `${VEGAS_MATT_SITE_URL}/venues/agua-caliente-rancho-mirage-ca` },
      { id: 'Footer-Allegiant-Stadium-NV-Link', label: 'Allegiant Stadium', href: `${VEGAS_MATT_SITE_URL}/venues/allegiant-stadium-las-vegas-nv` },
      { id: 'Footer-All-In-Vegas-Matt-Premiere-YouTube-Channel-Link', label: 'All In: Vegas Matt Premiere', href: `${VEGAS_MATT_SITE_URL}/venues/all-in-vegas-matt-premiere-fanduelcasino-youtube-channel` },
      { id: 'Footer-Ballys-Lincoln-RI-Link', label: "Bally's Lincoln", href: `${VEGAS_MATT_SITE_URL}/venues/ballys-twin-river-lincoln-lincoln-ri` },
      { id: 'Footer-Black-Oak-CA-Link', label: 'Black Oak', href: `${VEGAS_MATT_SITE_URL}/venues/black-oak-casino-resort-tuolumne-ca` },
      { id: 'Footer-Detroit-Princess-MI-Link', label: 'Detroit Princess', href: `${VEGAS_MATT_SITE_URL}/venues/detroit-princess-riverboat-detroit-mi` },
      { id: 'Footer-El-Cortez-NV-Link', label: 'El Cortez', href: `${VEGAS_MATT_SITE_URL}/venues/el-cortez-hotel-and-casino-las-vegas-nv` },
      { id: 'Footer-Fontainebleau-NV-Link', label: 'Fontainebleau', href: `${VEGAS_MATT_SITE_URL}/venues/fontainebleau-las-vegas-las-vegas-nv` },
      { id: 'Footer-Graton-CA-Link', label: 'Graton', href: `${VEGAS_MATT_SITE_URL}/venues/graton-resort-casino-rohnert-park-ca` },
      { id: 'Footer-Greektown-MI-Link', label: 'Greektown', href: `${VEGAS_MATT_SITE_URL}/venues/hollywood-casino-at-greektown-detroit-mi` },
      { id: 'Footer-Hard-Rock-FL-FL-Link', label: 'Hard Rock, FL', href: `${VEGAS_MATT_SITE_URL}/venues/hard-rock-hotel-casino-hollywood-fl` },
      { id: 'Footer-Hard-Rock-NJ-NJ-Link', label: 'Hard Rock, NJ', href: `${VEGAS_MATT_SITE_URL}/venues/hard-rock-hotel-casino-atlantic-city-nj` },
      { id: 'Footer-Hard-Rock-Tampa-FL-Link', label: 'Hard Rock Tampa', href: `${VEGAS_MATT_SITE_URL}/venues/hard-rock-hotel-casino-tampa-tampa-fl` },
      { id: 'Footer-Mohegan-NV-Link', label: 'Mohegan', href: `${VEGAS_MATT_SITE_URL}/venues/mohegan-casino-at-virgin-hotels-las-vegas-nv` },
      { id: 'Footer-Mohegan-Sun-CT-Link', label: 'Mohegan Sun', href: `${VEGAS_MATT_SITE_URL}/venues/mohegan-sun-casino-resort-uncasville-ct` },
      { id: 'Footer-Montego-Bay-NV-Link', label: 'Montego Bay', href: `${VEGAS_MATT_SITE_URL}/venues/montego-bay-wendover-nv` },
      { id: 'Footer-Morongo-CA-Link', label: 'Morongo', href: `${VEGAS_MATT_SITE_URL}/venues/morongo-casino-resort-spa-cabazon-ca` },
      { id: 'Footer-MotorCity-MI-Link', label: 'MotorCity', href: `${VEGAS_MATT_SITE_URL}/venues/motorcity-casino-hotel-detroit-mi` },
      { id: 'Footer-Ocean-NJ-Link', label: 'Ocean', href: `${VEGAS_MATT_SITE_URL}/venues/ocean-casino-resort-atlantic-city-nj` },
      { id: 'Footer-Peppermill-Reno-NV-Link', label: 'Peppermill Reno', href: `${VEGAS_MATT_SITE_URL}/venues/peppermill-resort-spa-casino-reno-reno-nv` },
      { id: 'Footer-Peppermill-Wendover-NV-Link', label: 'Peppermill Wendover', href: `${VEGAS_MATT_SITE_URL}/venues/peppermill-hotel-casino-wendover-west-wendover-nv` },
      { id: 'Footer-Potawatomi-WI-Link', label: 'Potawatomi', href: `${VEGAS_MATT_SITE_URL}/venues/potawatomi-casino-hotel-milwaukee-wi` },
      { id: 'Footer-Rainbow-Wendover-NV-Link', label: 'Rainbow Wendover', href: `${VEGAS_MATT_SITE_URL}/venues/rainbow-hotel-casino-west-wendover-nv` },
      { id: 'Footer-Seneca-Allegany-NY-Link', label: 'Seneca Allegany', href: `${VEGAS_MATT_SITE_URL}/venues/seneca-allegany-resort-casino-salamanca-ny` },
      { id: 'Footer-Seneca-Buffalo-NY-Link', label: 'Seneca Buffalo', href: `${VEGAS_MATT_SITE_URL}/venues/seneca-buffalo-creek-casino-buffalo-ny` },
      { id: 'Footer-Seneca-Niagara-NY-Link', label: 'Seneca Niagara', href: `${VEGAS_MATT_SITE_URL}/venues/seneca-niagara-resort-casino-niagara-falls-ny` },
      { id: 'Footer-Viejas-CA-Link', label: 'Viejas', href: `${VEGAS_MATT_SITE_URL}/venues/viejas-casino-resort-alpine-ca` },
      { id: 'Footer-Virgin-Los-Angeles-CA-Link', label: 'Virgin Los Angeles', href: `${VEGAS_MATT_SITE_URL}/venues/virgin-voyages-los-angeles-san-pedro-ca` },
      { id: 'Footer-Virgin-Miami-FL-Link', label: 'Virgin Miami', href: `${VEGAS_MATT_SITE_URL}/venues/virgin-voyages-miami-miami-fl` },
      { id: 'Footer-Virgin-San-Juan-PR-Link', label: 'Virgin San Juan', href: `${VEGAS_MATT_SITE_URL}/venues/virgin-voyages-san-juan-san-juan-pr` },
      { id: 'Footer-Virgin-Seattle-WA-Link', label: 'Virgin Seattle', href: `${VEGAS_MATT_SITE_URL}/venues/virgin-voyages-seattle-seattle-wa` },
      { id: 'Footer-Wild-Horse-Pass-AZ-Link', label: 'Wild Horse Pass', href: `${VEGAS_MATT_SITE_URL}/venues/wild-horse-pass-resort-casino-chandler-az` },
    ],
  },
];
const VEGAS_MATT_LOGO_PATH =
  'M0 416.2h89.9V202.7L.2 6 0 416.2zM568.9 0c-17.2 0-31.1 13.9-31.1 31.1s6.5 21.9 16.1 27.2l-30.8 208.8h-16.3V6c-.1 0-89.8 199.3-89.8 199.3v211h89.9v-67.1H560v-82h-18.2L572 62.1c15.7-1.6 27.9-14.8 27.9-30.9S586.1 0 568.9 0zM449.8 1.1c-46.1 0-85.9 27.3-104.1 66.6l-90.6 210.9-90.6-210.9c-18.2-39.3-58-66.6-104.1-66.6h-26l180.8 415.8c-1.8-1.8 79.9 0 79.9 0L475.8 1.1h-26z';
const VEGAS_MATT_MENU_PATH = 'M0 0h612v87.4H0V0zm0 218.6h612V306H0v-87.4zm612 218.5v87.4H0v-87.4h612z';
const FOOTER_COPYRIGHT_ICON_PATH =
  'M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM199.4 312.6c31.2 31.2 81.9 31.2 113.1 0c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9c-50 50-131 50-181 0s-50-131 0-181s131-50 181 0c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0c-31.2-31.2-81.9-31.2-113.1 0s-31.2 81.9 0 113.1z';
const HOME1_SIDEBET_PRIORITY_ORDER = ['VM', 'EJ', 'WBG', 'W2J'];
const HOME1_SOCIAL_SAYINGS_MAP = {
  sando: 'Sando!',
  idiot_counter: 'Idiot',
  lets_enjoy_this: "Let's Enjoy This!",
  fire_it_in: 'Fire it in there!',
  thats_what_she_said: "That's what she said!",
  innage: 'Innage',
  was_it_over_when_the_germans: 'Was it over when the Germans?',
  now_were_loggin: "Now we're loggin'!",
  how_much_was_that: 'How much was that?',
  poke_in_the_eye: 'Better than a poke in the eye!',
  sure_get_5_a_lot: 'You sure get 5 a lot!',
  those_should_be_wild: 'Those should be wild!',
  jingles: 'Jingles',
  coins: 'Coins',
  thats_a_massive_guys: "That's a massive guys",
  baby_doll: 'Baby Doll (W2J)',
  a_lot_of_people_are_saying_it: 'A lot of people are saying it!',
  flags: 'Flags!',
  balls: 'Balls!',
  hats: 'Hats!',
};
const HOME1_SOCIAL_TRENDS_MAP = {
  big_checks: 'Big Checks',
  spirit_fingers: 'Spirit Fingers',
  victory_laps: 'Victory Laps',
  triple_pops: 'Triple Pops',
  wbg_belly_laughs: 'WBG Belly Laughs',
  crickets: 'Crickets',
  bonu_in_a_bonu: 'Bonu in a Bonu',
  first_spin_johnson: 'First Spin Johnson',
  next_spin_johnson: 'Next Spin Johnson',
  accidental_fast_spins: 'Accidental Fast Spins',
  vm_wears_chain: 'VM Wears Chain',
  ej_remembers_to_add_content: 'EJ Remembers To Add Content',
  blazed_it_and_420: 'Blazed it / 420',
  vm_story_triggers_a_bonus: 'VM Story Triggers A Bonus',
  wbg_pun_counter: 'WBG Puns',
  double_pops: 'Double Pops',
  bbbs: 'Big Beautiful Buffalo Spins',
};
const HOME1_MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const HOME1_TICKER_LOADING_FACTS = [
  {
    key: 'loading-position',
    label: 'Current position',
    valueLabel: 'Loading',
    note: 'Reading the latest board',
    targetId: 'home1-run',
  },
  {
    key: 'loading-streak',
    label: 'Longest winning streak',
    valueLabel: 'Loading',
    note: 'Building streak facts',
    targetId: 'home1-streaks',
  },
  {
    key: 'loading-days',
    label: 'Tracked days',
    valueLabel: 'Loading',
    note: 'Counting daily movement',
    targetId: 'home1-calendar',
  },
];

const formatCurrency = (value, { includeCents = false } = {}) => {
  if (value == null || Number.isNaN(value)) return 'Loading';
  const sign = value < 0 ? '-' : '';
  const absolute = Math.abs(value);
  return `${sign}$${absolute.toLocaleString(undefined, {
    minimumFractionDigits: includeCents ? 2 : 0,
    maximumFractionDigits: includeCents ? 2 : 0,
  })}`;
};

const formatCompactCurrency = (value) => {
  if (value == null || Number.isNaN(value)) return 'Loading';
  const sign = value < 0 ? '-' : '';
  const absolute = Math.abs(value);

  if (absolute >= 1000000) {
    return `${sign}$${(absolute / 1000000).toFixed(1)}M`;
  }

  if (absolute >= 1000) {
    return `${sign}$${Math.round(absolute / 1000)}K`;
  }

  return `${sign}$${Math.round(absolute)}`;
};

const getToneFromValue = (value) => {
  if (value == null || Number.isNaN(value)) return 'neutral';
  return value > 0 ? 'positive' : value < 0 ? 'negative' : 'neutral';
};

const formatDayCount = (value) => {
  if (value == null || Number.isNaN(value)) return 'Loading';
  const roundedValue = Math.round(value);
  return `${roundedValue} day${roundedValue === 1 ? '' : 's'}`;
};

const formatPercent = (value, total) => {
  if (!total) return '0%';
  return `${Math.round((value / total) * 100)}%`;
};

const formatShortDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.valueOf())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

const toDateKey = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.valueOf())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const cleanSideBetCurrency = (value) => {
  if (value == null) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return +trimmed.replace(/\(/g, '-').replace(/\)/g, '').replace(/[$,]/g, '');
};

const isMissingNumber = (value) => value == null || Number.isNaN(Number(value));

const formatInteger = (value) => {
  if (isMissingNumber(value)) return 'Loading';
  return Math.round(value).toLocaleString();
};

const formatTickerDays = (value) => {
  if (isMissingNumber(value)) return 'Loading';
  return formatDayCount(Math.round(value));
};

const formatTickerPercent = (value) => {
  if (isMissingNumber(value)) return 'Loading';
  return `${Math.round(value)}%`;
};

const shuffleTickerFacts = (facts) =>
  facts
    .map((fact) => ({ fact, order: Math.random() }))
    .sort((a, b) => a.order - b.order)
    .map(({ fact }) => fact);

const buildHomeTickerFacts = ({
  stats,
  streakStats,
  positionLabel,
  positionToDateLabel,
  streakLabel,
  streakTotalDays,
  activeStreakDays,
  activeStreakDirection,
  activeStreakTone,
  sideBetSummary,
  bonusStats,
  topBonuses = [],
  favoriteSlots = [],
  tableStats,
  gameCounts = [],
  communitySummary,
  socialSayings = [],
  socialTrends = [],
  gruntStats,
  communityLoaded = false,
}) => {
  const activeStreakLabel =
    activeStreakDirection === 'Even' ? 'Current streak' : `Current ${activeStreakDirection.toLowerCase()} streak`;
  const winRate = streakTotalDays ? (streakStats.winningDays / streakTotalDays) * 100 : null;
  const rangeNote = streakLabel.toLowerCase();
  const worstRunNote = `Worst run of ${rangeNote}`;
  const highestBalanceNote = `Highest balance, ${positionToDateLabel.toLowerCase()}`;
  const sideBetLeader = sideBetSummary?.leader;
  const sideBetTrailer = sideBetSummary?.trailer;
  const topBonus = topBonuses[0];
  const favoriteSlot = favoriteSlots[0];
  const topTableGame = gameCounts[0];
  const topSaying = socialSayings[0];
  const topTrend = socialTrends[0];
  const hasSideBetStats = Boolean(sideBetSummary?.totalPlayers);
  const hasBonusStats = Boolean(bonusStats?.sessionCount);
  const hasTableStats = Boolean(tableStats?.playedSessions);
  const hasCommunityStats = Boolean(communityLoaded);

  return [
    {
      key: 'current-position',
      label: 'Current position',
      value: stats.currentPosition,
      type: 'currency',
      tone: getToneFromValue(stats.currentPosition),
      note: positionToDateLabel,
      targetId: 'home1-run',
    },
    {
      key: 'high-water-mark',
      label: 'High water mark',
      value: stats.highWaterMark,
      type: 'currency',
      tone: getToneFromValue(stats.highWaterMark),
      note: highestBalanceNote,
      targetId: 'home1-run',
    },
    {
      key: 'theoretical-high-water-mark',
      label: 'Theoretical HWM',
      value: stats.theoreticalHighWaterMark,
      type: 'currency',
      tone: getToneFromValue(stats.theoreticalHighWaterMark),
      note: 'If they quit at the high water mark each day',
      targetId: 'home1-run',
    },
    {
      key: 'current-status',
      label: 'Current status',
      valueLabel: stats.statusText,
      tone: stats.statusTone,
      note: 'Latest position direction',
      targetId: 'home1-run',
    },
    {
      key: 'active-streak',
      label: activeStreakLabel,
      value: activeStreakDays,
      formatter: formatTickerDays,
      tone: activeStreakTone,
      note: activeStreakDays ? `${streakLabel} latest run` : `${streakLabel} is between streaks`,
      targetId: 'home1-streaks',
    },
    {
      key: 'longest-winning-streak',
      label: 'Longest winning streak',
      value: streakStats.longestWinningStreak,
      formatter: formatTickerDays,
      tone: 'positive',
      note: 'Best run in a row',
      targetId: 'home1-streaks',
    },
    {
      key: 'longest-losing-streak',
      label: 'Longest losing streak',
      value: streakStats.longestLosingStreak,
      formatter: formatTickerDays,
      tone: 'negative',
      note: worstRunNote,
      targetId: 'home1-streaks',
    },
    {
      key: 'biggest-winning-day',
      label: 'Biggest winning day',
      value: stats.biggestWin,
      type: 'currency',
      tone: 'positive',
      note: positionToDateLabel,
      targetId: 'home1-streaks',
    },
    {
      key: 'biggest-losing-day',
      label: 'Biggest losing day',
      value: stats.biggestLoss,
      type: 'currency',
      tone: 'negative',
      note: positionToDateLabel,
      targetId: 'home1-streaks',
    },
    {
      key: 'winning-days',
      label: 'Winning days',
      value: streakStats.winningDays,
      tone: 'positive',
      note: streakLabel,
      targetId: 'home1-calendar',
    },
    {
      key: 'losing-days',
      label: 'Losing days',
      value: streakStats.losingDays,
      tone: 'negative',
      note: streakLabel,
      targetId: 'home1-calendar',
    },
    {
      key: 'break-even-days',
      label: 'Break even days',
      value: streakStats.evenDays,
      tone: 'neutral',
      note: streakLabel,
      targetId: 'home1-calendar',
    },
    {
      key: 'tracked-days',
      label: 'Tracked days',
      value: streakTotalDays,
      note: 'Win, loss, and even days',
      targetId: 'home1-calendar',
    },
    {
      key: 'win-rate',
      label: 'Win rate',
      value: winRate,
      formatter: formatTickerPercent,
      tone: 'positive',
      note: streakTotalDays ? `${streakStats.winningDays} of ${streakTotalDays} tracked days` : 'No tracked days',
      targetId: 'home1-streaks',
    },
    hasSideBetStats && {
      key: 'side-bet-leader',
      label: 'Side-bet leader',
      value: sideBetLeader?.total,
      type: 'currency',
      tone: getToneFromValue(sideBetLeader?.total),
      note: `${sideBetLeader?.name || 'Leader'} in ${rangeNote}`,
      targetId: 'home1-sidebets',
    },
    hasSideBetStats && {
      key: 'side-bet-low',
      label: 'Side-bet low',
      value: sideBetTrailer?.total,
      type: 'currency',
      tone: getToneFromValue(sideBetTrailer?.total),
      note: `${sideBetTrailer?.name || 'Low'} in ${rangeNote}`,
      targetId: 'home1-sidebets',
    },
    hasSideBetStats && {
      key: 'side-bet-action',
      label: 'Side-bet action',
      value: sideBetSummary.totalAction,
      type: 'currency',
      compact: true,
      tone: 'neutral',
      note: `${sideBetSummary.totalPlayers} players tracked`,
      targetId: 'home1-sidebets',
    },
    hasSideBetStats && {
      key: 'side-bet-winners',
      label: 'Side-bet winners',
      value: sideBetSummary.winningPlayers,
      tone: 'positive',
      note: `${sideBetSummary.losingPlayers} players down`,
      targetId: 'home1-sidebets',
    },
    hasBonusStats && {
      key: 'qotd-success',
      label: 'QOTD success',
      value: bonusStats.qotdPercent,
      formatter: formatHome1Percent,
      tone: 'positive',
      note: `${bonusStats.sessionCount} tracked sessions`,
      targetId: 'home1-bonuses',
    },
    hasBonusStats && {
      key: 'average-bonuses',
      label: 'Average bonuses',
      value: bonusStats.averageBonuses,
      formatter: formatHome1Decimal,
      tone: 'positive',
      note: 'Per tracked session',
      targetId: 'home1-bonuses',
    },
    hasBonusStats && {
      key: 'total-bonuses',
      label: 'Total bonuses',
      value: bonusStats.totalBonuses,
      tone: 'positive',
      note: `${rangeNote} bonus count`,
      targetId: 'home1-bonuses',
    },
    hasBonusStats && {
      key: 'most-bonuses',
      label: 'Most in a session',
      value: bonusStats.mostBonuses,
      tone: 'positive',
      note: 'Highest bonus count',
      targetId: 'home1-bonuses',
    },
    hasBonusStats && {
      key: 'total-handpays',
      label: 'Total handpays',
      value: bonusStats.totalHandpays,
      tone: 'neutral',
      note: `${rangeNote} handpays`,
      targetId: 'home1-bonuses',
    },
    topBonus && {
      key: 'largest-bonus',
      label: 'Largest bonus',
      value: topBonus.amount,
      type: 'currency',
      tone: 'positive',
      note: `${topBonus.label} ${formatShortDate(topBonus.date)}`,
      targetId: 'home1-bonuses',
    },
    favoriteSlot && {
      key: 'favorite-slot',
      label: 'Favorite slot',
      value: favoriteSlot.count,
      tone: 'positive',
      note: favoriteSlot.label,
      targetId: 'home1-bonuses',
    },
    hasTableStats && {
      key: 'table-current-position',
      label: 'Table position',
      value: tableStats.current,
      type: 'currency',
      tone: getToneFromValue(tableStats.current),
      note: `${tableStats.playedSessions} sessions with action`,
      targetId: 'home1-table-games',
    },
    hasTableStats && {
      key: 'table-biggest-win',
      label: 'Table biggest win',
      value: tableStats.biggestWin,
      type: 'currency',
      tone: 'positive',
      note: 'Best table session',
      targetId: 'home1-table-games',
    },
    hasTableStats && {
      key: 'table-biggest-loss',
      label: 'Table biggest loss',
      value: tableStats.biggestLoss,
      type: 'currency',
      tone: tableStats.biggestLoss < 0 ? 'negative' : 'neutral',
      note: 'Largest table reset',
      targetId: 'home1-table-games',
    },
    hasTableStats && {
      key: 'table-win-rate',
      label: 'Table win rate',
      value: tableStats.winPercent,
      formatter: formatHome1Percent,
      tone: 'positive',
      note: 'Non-zero table sessions',
      targetId: 'home1-table-games',
    },
    hasTableStats && {
      key: 'table-played-sessions',
      label: 'Table sessions',
      value: tableStats.playedSessions,
      tone: 'neutral',
      note: 'Sessions with table action',
      targetId: 'home1-table-games',
    },
    topTableGame && {
      key: 'top-table-game',
      label: 'Top table game',
      value: topTableGame.count,
      tone: 'neutral',
      note: topTableGame.label,
      targetId: 'home1-table-games',
    },
    hasCommunityStats && {
      key: 'big-check-amount',
      label: 'Big check amount',
      value: communitySummary?.bigCheckAmount,
      type: 'currency',
      compact: true,
      tone: 'positive',
      note: 'Community board total',
      targetId: 'home1-community',
    },
    hasCommunityStats && {
      key: 'big-checks',
      label: 'Big checks',
      value: communitySummary?.bigChecks,
      tone: 'positive',
      note: 'Big check count',
      targetId: 'home1-community',
    },
    hasCommunityStats && {
      key: 'triple-pops',
      label: 'Triple pops',
      value: communitySummary?.triplePops,
      tone: 'positive',
      note: 'Tracked moments',
      targetId: 'home1-community',
    },
    hasCommunityStats && {
      key: 'one-ticketers',
      label: 'One ticketers',
      value: communitySummary?.oneTicketersSlot,
      tone: 'neutral',
      note: 'Slot one-ticketer count',
      targetId: 'home1-community',
    },
    topSaying && {
      key: 'top-saying',
      label: 'Top saying',
      value: topSaying.value,
      tone: 'positive',
      note: topSaying.label,
      targetId: 'home1-community',
    },
    topTrend && {
      key: 'top-trend',
      label: 'Top trend',
      value: topTrend.value,
      tone: 'positive',
      note: topTrend.label,
      targetId: 'home1-community',
    },
    hasCommunityStats && {
      key: 'ej-grunts',
      label: 'EJ grunts',
      value: gruntStats?.total,
      tone: 'neutral',
      note: `${formatHome1Percent(gruntStats?.successPct ?? 0)} success`,
      targetId: 'home1-community',
    },
    hasCommunityStats && {
      key: 'grunt-success',
      label: 'Grunt success',
      value: gruntStats?.successPct,
      formatter: formatHome1Percent,
      tone: 'positive',
      note: `${formatInteger(gruntStats?.successful ?? 0)} successful`,
      targetId: 'home1-community',
    },
  ]
    .filter(Boolean)
    .filter((fact) => fact.valueLabel || !isMissingNumber(fact.value));
};

const buildHomeStats = (mainSeries, theoreticalSeries) => {
  const defaults = {
    currentPosition: null,
    highWaterMark: null,
    theoreticalHighWaterMark: null,
    statusText: 'Loading',
    statusTone: 'neutral',
    biggestWin: null,
    biggestLoss: null,
    biggestWinDay: null,
    biggestLossDay: null,
    winningDays: 0,
    losingDays: 0,
    evenDays: 0,
    currentWinningStreak: 0,
    currentLosingStreak: 0,
    longestWinningStreak: 0,
    longestLosingStreak: 0,
    streakDescription: 'Waiting on the latest spin',
    streakIntensity: 0,
    heatmapEntries: [],
  };

  if (!mainSeries.length) return defaults;

  const latest = mainSeries[mainSeries.length - 1];
  const currentPosition = latest.cumulative;
  const highWaterMark = Math.max(...mainSeries.map((entry) => entry.cumulative));
  const theoreticalHighWaterMark = theoreticalSeries.length
    ? theoreticalSeries[theoreticalSeries.length - 1].hwm
    : null;

  let biggestWinDay = null;
  let biggestLossDay = null;
  let winningDays = 0;
  let losingDays = 0;
  let evenDays = 0;
  let currentWinningStreak = 0;
  let currentLosingStreak = 0;
  let currentWinRun = 0;
  let currentLossRun = 0;
  let longestWinningStreak = 0;
  let longestLosingStreak = 0;

  mainSeries.forEach((entry, index) => {
    if (index === 0) return;
    const change = entry.change ?? entry.cumulative - mainSeries[index - 1].cumulative;

    if (change > 0) {
      winningDays += 1;
      if (!biggestWinDay || change > biggestWinDay.change) biggestWinDay = { date: entry.date, change };
      currentWinRun += 1;
      currentLossRun = 0;
      longestWinningStreak = Math.max(longestWinningStreak, currentWinRun);
    } else if (change < 0) {
      losingDays += 1;
      if (!biggestLossDay || change < biggestLossDay.change) biggestLossDay = { date: entry.date, change };
      currentLossRun += 1;
      currentWinRun = 0;
      longestLosingStreak = Math.max(longestLosingStreak, currentLossRun);
    } else {
      evenDays += 1;
      currentWinRun = 0;
      currentLossRun = 0;
    }
  });

  for (let i = mainSeries.length - 1; i >= 0; i -= 1) {
    const change = mainSeries[i].change ?? (i > 0 ? mainSeries[i].cumulative - mainSeries[i - 1].cumulative : 0);
    if (change > 0) {
      currentWinningStreak += 1;
    } else {
      break;
    }
  }

  for (let i = mainSeries.length - 1; i >= 0; i -= 1) {
    const change = mainSeries[i].change ?? (i > 0 ? mainSeries[i].cumulative - mainSeries[i - 1].cumulative : 0);
    if (change < 0) {
      currentLosingStreak += 1;
    } else {
      break;
    }
  }

  return {
    currentPosition,
    highWaterMark,
    theoreticalHighWaterMark,
    statusText: currentPosition > 0 ? 'Up' : currentPosition < 0 ? 'Down' : 'Even',
    statusTone: currentPosition > 0 ? 'positive' : currentPosition < 0 ? 'negative' : 'neutral',
    biggestWin: biggestWinDay?.change ?? null,
    biggestLoss: biggestLossDay?.change ?? null,
    biggestWinDay,
    biggestLossDay,
    winningDays,
    losingDays,
    evenDays,
    currentWinningStreak,
    currentLosingStreak,
    longestWinningStreak,
    longestLosingStreak,
    streakDescription:
      currentWinningStreak > 0
        ? `${currentWinningStreak} day${currentWinningStreak === 1 ? '' : 's'} on a winning streak`
        : currentLosingStreak > 0
        ? `${currentLosingStreak} day${currentLosingStreak === 1 ? '' : 's'} on a losing streak`
        : 'No streak yet - reload for the next slate',
    streakIntensity: Math.min(100, Math.max(0, currentWinningStreak * 25)),
    heatmapEntries: mainSeries.map((entry) => ({ date: entry.date, change: entry.change ?? 0 })),
  };
};

const mergeHome1SideBetTotals = (rows) =>
  rows.reduce((totals, row) => {
    Object.entries(row).forEach(([name, rawValue]) => {
      if (!name) return;
      const amount = cleanSideBetCurrency(rawValue);
      if (amount == null || Number.isNaN(amount)) return;
      totals[name] = (totals[name] ?? 0) + amount;
    });
    return totals;
  }, {});

const getHome1SideBetUrls = (selectedYear) => {
  if (selectedYear == null) return HOME1_SIDEBETS_CSVS;
  const url = SIDEBETS_CSV_BY_YEAR[selectedYear];
  return url ? [url] : [];
};

const parseHome1SocialNumber = (value) => {
  if (value == null) return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value !== 'string') return 0;
  const cleaned = value.replace(/[^0-9.-]/g, '');
  if (!cleaned) return 0;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
};

const mergeHome1SocialRows = (rows) =>
  rows.reduce((totals, row) => {
    Object.entries(row).forEach(([key, rawValue]) => {
      if (!key) return;
      totals[key] = (totals[key] ?? 0) + parseHome1SocialNumber(rawValue);
    });
    return totals;
  }, {});

const getHome1SocialUrls = (selectedYear) => {
  if (selectedYear == null) return HOME1_SOCIAL_CSVS;
  const url = SOCIAL_CSV_BY_YEAR[selectedYear];
  return url ? [url] : [];
};

const buildHome1SocialRows = (row, mapping, limit = 6) => {
  if (!row) return [];
  return Object.entries(mapping)
    .map(([key, label]) => ({
      key,
      label,
      value: parseHome1SocialNumber(row[key]),
    }))
    .filter((entry) => entry.value > 0)
    .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label))
    .slice(0, limit);
};

const buildHome1SocialSummary = (row) => ({
  bigCheckAmount: parseHome1SocialNumber(row?.big_check_amount),
  bigChecks: parseHome1SocialNumber(row?.big_checks),
  triplePops: parseHome1SocialNumber(row?.triple_pops),
  oneTicketersSlot: parseHome1SocialNumber(row?.one_ticketers_slot),
});

const buildHome1GruntStats = (row) => {
  const total = parseHome1SocialNumber(row?.ej_grunts_total);
  const successful = parseHome1SocialNumber(row?.ej_grunt_bonus);
  const unsuccessful = Math.max(total - successful, 0);

  return {
    total,
    successful,
    unsuccessful,
    successPct: total ? (successful / total) * 100 : 0,
  };
};

const sortHome1SideBetPlayers = (players) => {
  const priorityPlayers = players
    .filter((player) => HOME1_SIDEBET_PRIORITY_ORDER.includes(player.name))
    .sort((a, b) => HOME1_SIDEBET_PRIORITY_ORDER.indexOf(a.name) - HOME1_SIDEBET_PRIORITY_ORDER.indexOf(b.name));
  const otherPlayers = players
    .filter((player) => !HOME1_SIDEBET_PRIORITY_ORDER.includes(player.name))
    .sort((a, b) => a.name.localeCompare(b.name));
  return [...priorityPlayers, ...otherPlayers];
};

const buildHome1SideBetSummary = (players) => {
  if (!players.length) {
    return {
      leader: null,
      trailer: null,
      totalPlayers: 0,
      winningPlayers: 0,
      losingPlayers: 0,
      evenPlayers: 0,
      totalAction: 0,
    };
  }

  return {
    leader: players.reduce((max, player) => (player.total > max.total ? player : max), players[0]),
    trailer: players.reduce((min, player) => (player.total < min.total ? player : min), players[0]),
    totalPlayers: players.length,
    winningPlayers: players.filter((player) => player.total > 0).length,
    losingPlayers: players.filter((player) => player.total < 0).length,
    evenPlayers: players.filter((player) => player.total === 0).length,
    totalAction: players.reduce((sum, player) => sum + Math.abs(player.total), 0),
  };
};

const normalizeHome1ListLabel = (value) => (value || 'Unknown').replace(/\s+/g, ' ').trim();

const buildHome1CountRows = (rows, field, limit = 5) => {
  const counts = new Map();

  rows.forEach((row) => {
    if (!row[field]) return;
    row[field]
      .split(',')
      .map((item) => normalizeHome1ListLabel(item))
      .filter(Boolean)
      .forEach((item) => {
        counts.set(item, (counts.get(item) || 0) + 1);
      });
  });

  return Array.from(counts, ([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
    .slice(0, limit);
};

const buildHome1BonusStats = (rows) => {
  const sessionCount = rows.length;
  const totalBonuses = rows.reduce((sum, row) => sum + (row.numBonuses || 0), 0);
  const bonusCounts = rows.map((row) => row.numBonuses || 0);
  const positiveBonusCounts = bonusCounts.filter((value) => value > 0);
  const qotdSuccesses = rows.filter((row) => row.qotdSuccess).length;
  const totalHandpays = rows.reduce((sum, row) => sum + (row.handpays || 0), 0);

  return {
    sessionCount,
    totalBonuses,
    averageBonuses: sessionCount ? totalBonuses / sessionCount : 0,
    mostBonuses: bonusCounts.length ? Math.max(...bonusCounts) : 0,
    fewestBonuses: positiveBonusCounts.length ? Math.min(...positiveBonusCounts) : 0,
    qotdPercent: sessionCount ? (qotdSuccesses / sessionCount) * 100 : 0,
    totalHandpays,
  };
};

const buildHome1TopBonuses = (rows, limit = 5) =>
  rows
    .filter((row) => row.largestBonusAmount > 0)
    .sort((a, b) => b.largestBonusAmount - a.largestBonusAmount)
    .slice(0, limit)
    .map((row) => ({
      label: normalizeHome1ListLabel(row.largestBonusSlot),
      amount: row.largestBonusAmount,
      date: row.date,
    }));

const buildHome1TableSeries = (rows) => {
  let cumulative = 0;

  return rows
    .filter((row) => typeof row.tableGamesTotal === 'number' && !Number.isNaN(row.tableGamesTotal))
    .map((row) => {
      const delta = row.tableGamesTotal;
      cumulative += delta;
      const games = row.tableGames
        ? row.tableGames
            .split(',')
            .map((game) => normalizeHome1ListLabel(game))
            .filter(Boolean)
        : [];

      return {
        date: row.date,
        delta,
        cumulative,
        games,
      };
    });
};

const buildHome1TableStats = (series) => {
  const played = series.filter((entry) => entry.delta !== 0);
  const wins = played.filter((entry) => entry.delta > 0).length;

  return {
    current: series.length ? series[series.length - 1].cumulative : 0,
    biggestWin: played.length ? Math.max(...played.map((entry) => entry.delta)) : 0,
    biggestLoss: played.length ? Math.min(...played.map((entry) => entry.delta)) : 0,
    winPercent: played.length ? (wins / played.length) * 100 : 0,
    playedSessions: played.length,
  };
};

const getTableCalendarStatus = (entry, isFuture) => {
  if (isFuture) return 'pending';
  if (!entry || !entry.games.length) return 'no-play';
  if (entry.delta > 0) return 'win';
  if (entry.delta < 0) return 'lose';
  return 'even';
};

const clampHome1Number = (value, min, max) => Math.min(max, Math.max(min, value));

const getTableHeatColor = (status, intensity) => {
  if (status === 'win') {
    return `hsl(142, 66%, ${32 + intensity * 14}%)`;
  }

  if (status === 'lose') {
    return `hsl(4, 82%, ${58 - intensity * 30}%)`;
  }

  if (status === 'no-play') {
    return 'rgba(255, 255, 255, 0.18)';
  }

  if (status === 'even') {
    return '#8ce7db';
  }

  return 'rgba(255, 255, 255, 0.12)';
};

const getHome1HeatRampColor = (value, minValue, maxValue) => {
  if (!Number.isFinite(value)) return 'rgba(255, 255, 255, 0.28)';
  if (!Number.isFinite(minValue) || !Number.isFinite(maxValue) || maxValue <= minValue) {
    return 'hsl(142, 66%, 34%)';
  }

  const ratio = clampHome1Number((value - minValue) / (maxValue - minValue), 0, 1);

  if (ratio < 0.5) {
    const redIntensity = 1 - ratio / 0.5;
    return `hsl(4, 82%, ${62 - redIntensity * 34}%)`;
  }

  const greenIntensity = (ratio - 0.5) / 0.5;
  return `hsl(142, 66%, ${62 - greenIntensity * 28}%)`;
};

const getHome1GreenHeatColor = (value, maxValue) => {
  if (!Number.isFinite(value) || !Number.isFinite(maxValue) || maxValue <= 0) return 'hsl(152, 66%, 64%)';
  const intensity = clampHome1Number(value / maxValue, 0, 1);
  return `hsl(142, 66%, ${66 - intensity * 32}%)`;
};

const buildHome1TableCalendarMonths = (series, year) => {
  const entryByDate = new Map();
  const yearEntries = series.filter((entry) => entry.date?.getFullYear() === year);
  const maxWin = Math.max(...yearEntries.filter((entry) => entry.delta > 0).map((entry) => entry.delta), 1);
  const maxLoss = Math.max(...yearEntries.filter((entry) => entry.delta < 0).map((entry) => Math.abs(entry.delta)), 1);

  yearEntries.forEach((entry) => {
    entryByDate.set(toDateKey(entry.date), entry);
  });

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const counts = { win: 0, lose: 0, even: 0, noPlay: 0, pending: 0 };

  const months = HOME1_MONTH_NAMES.map((name, monthIndex) => {
    const dayCount = new Date(year, monthIndex + 1, 0).getDate();
    const days = Array.from({ length: dayCount }, (_, dayIndex) => {
      const date = new Date(year, monthIndex, dayIndex + 1);
      const key = toDateKey(date);
      const isFuture = date > today;
      const entry = entryByDate.get(key) || null;
      const status = getTableCalendarStatus(entry, isFuture);
      const countKey = status === 'no-play' ? 'noPlay' : status;
      counts[countKey] += 1;
      const heatBase =
        status === 'win'
          ? Math.abs(entry.delta) / maxWin
          : status === 'lose'
          ? Math.abs(entry.delta) / maxLoss
          : 0;
      const heatIntensity = status === 'win' || status === 'lose' ? clampHome1Number(heatBase, 0.18, 1) : 0;

      return {
        key,
        date,
        delta: entry?.delta ?? 0,
        cumulative: entry?.cumulative ?? null,
        games: entry?.games ?? [],
        heatIntensity,
        heatColor: getTableHeatColor(status, heatIntensity),
        status,
      };
    });

    return { name, days };
  });

  return { months, counts };
};

const formatTableCalendarDayLabel = (day) => {
  if (day.status === 'pending') return `${formatShortDate(day.date)}: Pending`;
  if (day.status === 'no-play') return `${formatShortDate(day.date)}: Did not play`;
  if (day.status === 'even') return `${formatShortDate(day.date)}: Break even`;
  const statusLabel = day.status === 'win' ? 'Table win' : 'Table loss';
  return `${formatShortDate(day.date)}: ${statusLabel} (${formatCurrency(day.delta)})`;
};

const getTableDayResultLabel = (day) => {
  if (!day || day.status === 'pending') return 'Pending';
  if (day.status === 'no-play') return 'Did not play';
  return formatCurrency(day.delta);
};

const formatHome1Decimal = (value) => {
  if (isMissingNumber(value)) return 'Loading';
  return Number(value).toFixed(1);
};

const formatHome1Percent = (value) => {
  if (isMissingNumber(value)) return 'Loading';
  return `${Number(value).toFixed(1)}%`;
};

const getCalendarStatus = (change, hasResult) => {
  if (!hasResult) return 'pending';
  if (change > 0) return 'win';
  if (change < 0) return 'lose';
  return 'even';
};

const buildHomeCalendarMonths = (entries, year) => {
  const entryByDate = new Map();
  entries.forEach((entry) => {
    if (entry.date?.getFullYear() === year) {
      entryByDate.set(toDateKey(entry.date), entry);
    }
  });

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const counts = { win: 0, lose: 0, even: 0, pending: 0 };

  const months = HOME1_MONTH_NAMES.map((name, monthIndex) => {
    const dayCount = new Date(year, monthIndex + 1, 0).getDate();
    const days = Array.from({ length: dayCount }, (_, dayIndex) => {
      const date = new Date(year, monthIndex, dayIndex + 1);
      const key = toDateKey(date);
      const isFuture = date > today;
      const entry = entryByDate.get(key) || null;
      const hasResult = !isFuture && entryByDate.has(key);
      const change = hasResult ? entry.change ?? 0 : null;
      const status = getCalendarStatus(change, hasResult);
      counts[status] += 1;

      return {
        key,
        date,
        change,
        cumulative: hasResult ? entry?.cumulative ?? null : null,
        hasResult,
        status,
      };
    });

    return { name, days };
  });

  return { months, counts };
};

const formatCalendarDayLabel = (day) => {
  const statusLabel =
    day.status === 'win' ? 'Win' : day.status === 'lose' ? 'Lose' : day.status === 'even' ? 'Even' : 'Pending';
  const moveLabel = day.hasResult ? ` (${formatCurrency(day.change)})` : '';
  return `${formatShortDate(day.date)}: ${statusLabel}${moveLabel}`;
};

const getCalendarDayResultLabel = (day) => {
  if (!day || !day.hasResult) return 'Pending';
  return formatCurrency(day.change);
};

function useHome1SideBets(selectedYear) {
  const [state, setState] = useState({
    players: [],
    loading: false,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;

    async function loadSideBets() {
      setState((current) => ({ ...current, loading: true, error: null }));
      try {
        const urls = getHome1SideBetUrls(selectedYear);
        if (!urls.length) {
          throw new Error('No side bet data available for the selected year.');
        }

        const d3 = await loadExternalScript(HOME1_D3_URL, 'd3');
        if (cancelled) return;

        const totalsSets = await Promise.all(urls.map((url) => d3.csv(url, d3.autoType)));
        if (cancelled) return;

        const totalsRows = totalsSets.map((rows) => rows?.[0]).filter(Boolean);
        if (!totalsRows.length) {
          throw new Error('No side bet data available.');
        }

        const totalsRow = selectedYear == null && totalsRows.length > 1 ? mergeHome1SideBetTotals(totalsRows) : totalsRows[0];
        const players = Object.entries(totalsRow)
          .map(([name, rawValue]) => {
            const total = cleanSideBetCurrency(rawValue);
            if (!name || total == null || Number.isNaN(total)) return null;
            if (selectedYear != null && total === 0) return null;
            return { name, total };
          })
          .filter(Boolean);

        setState({
          players: sortHome1SideBetPlayers(players),
          loading: false,
          error: null,
        });
      } catch (err) {
        if (!cancelled) {
          setState({
            players: [],
            loading: false,
            error: err,
          });
        }
      }
    }

    loadSideBets();

    return () => {
      cancelled = true;
    };
  }, [selectedYear]);

  return state;
}

function useHome1Social(selectedYear) {
  const [state, setState] = useState({
    row: null,
    loading: false,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;

    async function loadSocialStats() {
      setState((current) => ({ ...current, loading: true, error: null }));
      try {
        const urls = getHome1SocialUrls(selectedYear);
        if (!urls.length) {
          throw new Error('No community data available for the selected year.');
        }

        const d3 = await loadExternalScript(HOME1_D3_URL, 'd3');
        if (cancelled) return;

        const socialSets = await Promise.all(urls.map((url) => d3.csv(url)));
        if (cancelled) return;

        const rows = socialSets.map((set) => set?.[0]).filter(Boolean);
        if (!rows.length) {
          throw new Error('No community data available.');
        }

        setState({
          row: selectedYear == null && rows.length > 1 ? mergeHome1SocialRows(rows) : rows[0],
          loading: false,
          error: null,
        });
      } catch (err) {
        if (!cancelled) {
          setState({
            row: null,
            loading: false,
            error: err,
          });
        }
      }
    }

    loadSocialStats();

    return () => {
      cancelled = true;
    };
  }, [selectedYear]);

  return state;
}

function Home1CalendarSection({ year, mainSeries, loading, error }) {
  const calendarSeries = useMemo(
    () => mainSeries.filter((entry) => entry.date.getFullYear() === year),
    [mainSeries, year],
  );
  const calendarStats = useMemo(() => buildHomeStats(calendarSeries, []), [calendarSeries]);
  const calendar = useMemo(() => buildHomeCalendarMonths(calendarSeries, year), [calendarSeries, year]);
  const allCalendarDays = useMemo(() => calendar.months.flatMap((month) => month.days), [calendar]);
  const [selectedCalendarDayKey, setSelectedCalendarDayKey] = useState(null);
  const selectedCalendarDay = useMemo(
    () => allCalendarDays.find((day) => day.key === selectedCalendarDayKey) || null,
    [allCalendarDays, selectedCalendarDayKey],
  );
  const calendarRef = useRef(null);
  const trackedDays = calendar.counts.win + calendar.counts.lose + calendar.counts.even;
  const selectedCalendarDayTone = selectedCalendarDay?.status ?? 'pending';
  const currentStreakTone =
    calendarStats.currentWinningStreak > 0
      ? 'positive'
      : calendarStats.currentLosingStreak > 0
      ? 'negative'
      : 'neutral';
  const currentStreakDays =
    calendarStats.currentWinningStreak > 0
      ? calendarStats.currentWinningStreak
      : calendarStats.currentLosingStreak > 0
      ? calendarStats.currentLosingStreak
      : 0;
  const currentStreakLabel =
    calendarStats.currentWinningStreak > 0
      ? 'Winning'
      : calendarStats.currentLosingStreak > 0
      ? 'Losing'
      : 'Even';
  const summaryCards = [
    {
      key: 'winning-days',
      label: 'Winning days',
      value: <CountUpNumber value={calendar.counts.win} />,
      caption: trackedDays ? `${formatPercent(calendar.counts.win, trackedDays)} of tracked days` : 'No tracked wins yet',
      tone: 'positive',
    },
    {
      key: 'losing-days',
      label: 'Losing days',
      value: <CountUpNumber value={calendar.counts.lose} />,
      caption: trackedDays ? `${formatPercent(calendar.counts.lose, trackedDays)} of tracked days` : 'No tracked losses yet',
      tone: 'negative',
    },
    {
      key: 'longest-win',
      label: 'Longest win streak',
      value: <CountUpDayCount value={calendarStats.longestWinningStreak} />,
      caption: `${year} best heater`,
      tone: 'positive',
    },
    {
      key: 'longest-loss',
      label: 'Longest loss streak',
      value: <CountUpDayCount value={calendarStats.longestLosingStreak} />,
      caption: `${year} longest reset`,
      tone: 'negative',
    },
    {
      key: 'current-streak',
      label: 'Current streak',
      value: currentStreakDays ? (
        <>
          {currentStreakLabel} <CountUpDayCount value={currentStreakDays} />
        </>
      ) : (
        'No active streak'
      ),
      caption: `${year} latest tracked result`,
      tone: currentStreakTone,
    },
  ];
  const calendarTitle = year === CURRENT_YEAR ? `${year} so far` : `${year} calendar`;

  useEffect(() => {
    setSelectedCalendarDayKey(null);
  }, [year]);

  useEffect(() => {
    if (!selectedCalendarDayKey) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setSelectedCalendarDayKey(null);
      }
    };

    const handlePointerDown = (event) => {
      if (!calendarRef.current || calendarRef.current.contains(event.target)) return;
      setSelectedCalendarDayKey(null);
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('pointerdown', handlePointerDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [selectedCalendarDayKey]);

  return (
    <RevealOnScroll className="home1-calendar-section" id="home1-calendar">
      <div className="home1-calendar-shell">
        <div className="home1-calendar-header">
          <div>
            <p className="home1-eyebrow">Calendar board</p>
            <h2>{calendarTitle}</h2>
            <p>Daily wins, losses, even days, and streak context by month.</p>
          </div>
        </div>

        <div className="home1-calendar-board" ref={calendarRef}>
          <div className="home1-calendar-topline">
            <div className="home1-calendar-legend" aria-label="Calendar result legend">
              <span>
                <i className="win" /> Win
              </span>
              <span>
                <i className="lose" /> Lose
              </span>
              <span>
                <i className="even" /> Even
              </span>
              <span>
                <i className="pending" /> Pending
              </span>
            </div>
            <strong>{loading ? 'Loading' : `${trackedDays} tracked days`}</strong>
          </div>

          {error ? <p className="home1-error">Unable to load calendar data.</p> : null}

          <div className="home1-calendar-summary">
            {summaryCards.map((card) => (
              <article className={`home1-calendar-summary-card ${card.tone}`} key={`${card.key}-${year}`}>
                <span>{card.label}</span>
                <strong>{loading ? 'Loading' : card.value}</strong>
                <small>{loading ? 'Reading the board' : card.caption}</small>
              </article>
            ))}
          </div>

          <div className="home1-calendar-months">
            {calendar.months.map((month, monthIndex) => {
              const isMonthActive = month.days.some((day) => day.key === selectedCalendarDayKey);

              return (
                <article
                  className={`home1-calendar-month${isMonthActive ? ' is-active' : ''}`}
                  key={month.name}
                  style={{ '--calendar-index': monthIndex }}
                >
                  <h3>{month.name}</h3>
                  <div className="home1-calendar-days">
                    {month.days.map((day) => {
                      const isSelected = selectedCalendarDay?.key === day.key;
                      const tooltipId = `home1-calendar-day-tooltip-${day.key}`;

                      return (
                        <span
                          className={`home1-calendar-day-wrap${isSelected ? ' is-active' : ''}`}
                          data-tooltip-align={
                            monthIndex % 3 === 0 ? 'start' : monthIndex % 3 === 2 ? 'end' : 'center'
                          }
                          key={day.key}
                        >
                          <button
                            type="button"
                            className={`home1-calendar-day ${day.status}${isSelected ? ' active' : ''}`}
                            title={formatCalendarDayLabel(day)}
                            aria-describedby={isSelected ? tooltipId : undefined}
                            aria-label={formatCalendarDayLabel(day)}
                            aria-pressed={isSelected}
                            onClick={() =>
                              setSelectedCalendarDayKey((currentKey) => (currentKey === day.key ? null : day.key))
                            }
                          />
                          {isSelected ? (
                            <div
                              className={`home1-tablegames-day-detail home1-calendar-day-detail ${selectedCalendarDayTone}`}
                              id={tooltipId}
                              role="tooltip"
                            >
                              <div className="home1-tablegames-day-detail-header">
                                <span>Selected day</span>
                                <strong>{formatShortDate(selectedCalendarDay.date)}</strong>
                              </div>
                              <div className="home1-tablegames-day-stats">
                                <article>
                                  <span>Daily result</span>
                                  <strong>{getCalendarDayResultLabel(selectedCalendarDay)}</strong>
                                </article>
                                <article>
                                  <span>Running position</span>
                                  <strong>
                                    {selectedCalendarDay.cumulative == null ? (
                                      '—'
                                    ) : (
                                      <CountUpCurrency value={selectedCalendarDay.cumulative} />
                                    )}
                                  </strong>
                                </article>
                              </div>
                            </div>
                          ) : null}
                        </span>
                      );
                    })}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </div>
    </RevealOnScroll>
  );
}

function Home1SideBetsSection({ players, loading, error, rangeLabel }) {
  const summary = useMemo(() => buildHome1SideBetSummary(players), [players]);
  const rankedPlayers = useMemo(
    () => [...players].sort((a, b) => b.total - a.total || a.name.localeCompare(b.name)),
    [players],
  );
  const mainPlayers = useMemo(
    () =>
      HOME1_SIDEBET_PRIORITY_ORDER.map((name) => players.find((player) => player.name === name)).filter(Boolean),
    [players],
  );
  const otherPlayers = useMemo(
    () => rankedPlayers.filter((player) => !HOME1_SIDEBET_PRIORITY_ORDER.includes(player.name)),
    [rankedPlayers],
  );
  const mainMaxTotal = mainPlayers.length ? Math.max(...mainPlayers.map((player) => Math.abs(player.total)), 1) : 1;
  const mainLeader = mainPlayers.length
    ? mainPlayers.reduce((max, player) => (player.total > max.total ? player : max), mainPlayers[0])
    : null;
  const graphLeader = mainLeader ? `${mainLeader.name} leads the pack` : `${summary.totalPlayers} players`;

  return (
    <RevealOnScroll className="home1-sidebets-section" id="home1-sidebets">
      <div className="home1-sidebets-shell">
        <div className="home1-sidebets-header">
          <div>
            <p className="home1-eyebrow">Side bets</p>
            <h2>Side Bet Standings</h2>
            <p>Track the action between the team, with the full table underneath.</p>
          </div>
        </div>

        <div className="home1-sidebets-board">
          <div className="home1-sidebets-board-header">
            <span>The VM Team</span>
            <strong>{loading ? 'Loading' : graphLeader}</strong>
          </div>

          {error ? <p className="home1-error">Unable to load side-bet totals.</p> : null}
          {!loading && !error && !mainPlayers.length ? (
            <p className="home1-sidebets-empty">No side-bet totals available yet.</p>
          ) : null}

          <div
            className="home1-sidebets-graph"
            key={`sidebets-${rangeLabel}-${mainPlayers.map((player) => `${player.name}:${player.total}`).join('|')}`}
            aria-label="The VM Team side bet totals graph"
          >
            <div className="home1-sidebets-axis" aria-hidden="true">
              <span>Down</span>
              <i>0</i>
              <span>Up</span>
            </div>
            {mainPlayers.map((player, index) => {
              const tone = getToneFromValue(player.total);
              const barSize = Math.max(3, (Math.abs(player.total) / mainMaxTotal) * 50);

              return (
                <article
                  className={`home1-sidebets-graph-row ${tone}`}
                  key={player.name}
                  style={{ '--sidebet-bar-size': `${barSize}%`, '--sidebet-index': index }}
                >
                  <strong className="home1-sidebets-main-name">{player.name}</strong>
                  <span className="home1-sidebets-graph-track" aria-hidden="true">
                    <i />
                  </span>
                  <span className="home1-sidebets-graph-value">
                    <CountUpCurrency value={player.total} />
                  </span>
                </article>
              );
            })}
          </div>

          <div className="home1-sidebets-others">
            <div className="home1-sidebets-board-header">
              <span>Others</span>
              <strong>{loading ? 'Loading' : `${otherPlayers.length} players`}</strong>
            </div>

            <div className="home1-sidebets-list">
              {otherPlayers.map((player, index) => {
                const tone = getToneFromValue(player.total);

                return (
                  <article className={`home1-sidebets-row ${tone}`} key={player.name}>
                    <span className="home1-sidebets-rank">{String(index + 1).padStart(2, '0')}</span>
                    <span className="home1-sidebets-name">{player.name}</span>
                    <strong>
                      <CountUpCurrency value={player.total} />
                    </strong>
                  </article>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </RevealOnScroll>
  );
}

function Home1BonusesSection({ loading, error, stats, topBonuses, favoriteSlots, animationKey }) {
  const maxBonusAmount = topBonuses.length ? Math.max(...topBonuses.map((bonus) => bonus.amount), 1) : 1;
  const maxSlotCount = favoriteSlots.length ? Math.max(...favoriteSlots.map((slot) => slot.count), 1) : 1;

  return (
    <RevealOnScroll className="home1-bonuses-section" id="home1-bonuses">
      <div className="home1-bonuses-shell">
        <div className="home1-bonuses-header">
          <div>
            <p className="home1-eyebrow">Bonuses</p>
            <h2>Bonus board</h2>
            <p>QOTD hit rate, bonus pace, handpays, and the biggest slot pops.</p>
          </div>
        </div>

        <div className="home1-position-stats home1-bonuses-stats">
          <PositionMetricCard
            label="QOTD Success"
            value={loading ? 'Loading' : <CountUpNumber value={stats.qotdPercent} formatter={formatHome1Percent} />}
            caption={`${stats.sessionCount} tracked sessions`}
            tone="positive"
            animationKey={animationKey}
          />
          <PositionMetricCard
            label="Average Bonuses"
            value={loading ? 'Loading' : <CountUpNumber value={stats.averageBonuses} formatter={formatHome1Decimal} />}
            caption="Bonuses per session"
            tone="positive"
            animationKey={animationKey}
          />
          <PositionMetricCard
            label="Most Bonuses In A Session"
            value={loading ? 'Loading' : <CountUpNumber value={stats.mostBonuses} />}
            caption="In a single session"
            tone="positive"
            animationKey={animationKey}
          />
          <PositionMetricCard
            label="Total Handpays"
            value={loading ? 'Loading' : <CountUpNumber value={stats.totalHandpays} />}
            caption="Total handpays"
            tone="neutral"
            animationKey={animationKey}
          />
        </div>

        <div className="home1-bonuses-layout">
          <div className="home1-bonuses-board">
            <div className="home1-sidebets-board-header">
              <span>Biggest Bonus Wins</span>
              <strong>{loading ? 'Loading' : `${topBonuses.length} slots`}</strong>
            </div>

            {error ? <p className="home1-error">Unable to load bonus data.</p> : null}
            {!loading && !error && !topBonuses.length ? (
              <p className="home1-sidebets-empty">No bonus hits available yet.</p>
            ) : null}

            <div className="home1-bonuses-list">
              {topBonuses.map((bonus, index) => (
                <article
                  className="home1-bonuses-row positive"
                  key={`${bonus.label}-${bonus.date?.toISOString?.() || index}`}
                  style={{
                    '--bonus-index': index,
                    '--bonus-bar-size': `${Math.max(4, (bonus.amount / maxBonusAmount) * 100)}%`,
                    '--bonus-heat-color': getHome1GreenHeatColor(bonus.amount, maxBonusAmount),
                  }}
                >
                  <span className="home1-sidebets-rank">{String(index + 1).padStart(2, '0')}</span>
                  <span className="home1-bonuses-name">{bonus.label}</span>
                  <span className="home1-bonuses-date">{formatShortDate(bonus.date)}</span>
                  <strong>
                    <CountUpCurrency value={bonus.amount} />
                  </strong>
                  <span className="home1-bonuses-track" aria-hidden="true">
                    <i />
                  </span>
                </article>
              ))}
            </div>
          </div>

          <div className="home1-bonuses-board home1-bonuses-board--compact">
            <div className="home1-sidebets-board-header">
              <span>Most Played Slots</span>
              <strong>{loading ? 'Loading' : <><CountUpNumber value={favoriteSlots.length} /> tracked</>}</strong>
            </div>

            <div className="home1-bonuses-slot-list">
              {favoriteSlots.map((slot, index) => (
                <article
                  className="home1-bonuses-slot-row"
                  key={slot.label}
                  style={{
                    '--bonus-index': index,
                    '--bonus-bar-size': `${Math.max(8, (slot.count / maxSlotCount) * 100)}%`,
                    '--bonus-heat-color': getHome1GreenHeatColor(slot.count, maxSlotCount),
                  }}
                >
                  <div>
                    <span>{slot.label}</span>
                    <strong className="home1-bonuses-slot-count">
                      <CountUpNumber value={slot.count} />
                      <em>sessions</em>
                    </strong>
                  </div>
                  <i aria-hidden="true" />
                </article>
              ))}
            </div>
          </div>
        </div>
      </div>
    </RevealOnScroll>
  );
}

function Home1TableGamesSection({
  loading,
  error,
  tableCalendarYear,
  stats,
  gameCounts,
  series,
  animationKey,
}) {
  const tableCalendar = useMemo(
    () => buildHome1TableCalendarMonths(series, tableCalendarYear),
    [series, tableCalendarYear],
  );
  const allTableDays = useMemo(() => tableCalendar.months.flatMap((month) => month.days), [tableCalendar]);
  const [selectedTableDayKey, setSelectedTableDayKey] = useState(null);
  const tableCalendarRef = useRef(null);
  const maxGameCount = gameCounts.length ? Math.max(...gameCounts.map((game) => game.count), 1) : 1;
  const currentTone = getToneFromValue(stats.current);
  const biggestLossTone = stats.biggestLoss < 0 ? 'negative' : 'neutral';
  const tableResultDays = tableCalendar.counts.win + tableCalendar.counts.lose + tableCalendar.counts.even;
  const selectedTableDay = useMemo(() => {
    return allTableDays.find((day) => day.key === selectedTableDayKey) || null;
  }, [allTableDays, selectedTableDayKey]);
  const selectedTableDayTone = selectedTableDay?.status || 'pending';

  useEffect(() => {
    if (!selectedTableDayKey) return;
    if (allTableDays.some((day) => day.key === selectedTableDayKey)) return;
    setSelectedTableDayKey(null);
  }, [allTableDays, selectedTableDayKey]);

  useEffect(() => {
    if (!selectedTableDayKey) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setSelectedTableDayKey(null);
      }
    };
    const handlePointerDown = (event) => {
      if (!tableCalendarRef.current || tableCalendarRef.current.contains(event.target)) return;
      setSelectedTableDayKey(null);
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [selectedTableDayKey]);

  return (
    <RevealOnScroll className="home1-tablegames-section" id="home1-table-games">
      <div className="home1-tablegames-shell">
        <div className="home1-tablegames-header">
          <div>
            <p className="home1-eyebrow">Table games</p>
            <h2>Table Action</h2>
            <p>Current table position, biggest swings, selected-day detail, and the table mix behind the run.</p>
          </div>
        </div>

        <div className="home1-position-stats home1-tablegames-stats">
          <PositionMetricCard
            label="Current Position"
            value={loading ? 'Loading' : <CountUpCurrency value={stats.current} />}
            caption={`${stats.playedSessions} sessions with table action`}
            tone={currentTone}
            animationKey={animationKey}
          />
          <PositionMetricCard
            label="Biggest Win"
            value={loading ? 'Loading' : <CountUpCurrency value={stats.biggestWin} />}
            caption="Best table-game session"
            tone="positive"
            animationKey={animationKey}
          />
          <PositionMetricCard
            label="Biggest Loss"
            value={loading ? 'Loading' : <CountUpCurrency value={stats.biggestLoss} />}
            caption="Largest table game loss"
            tone={biggestLossTone}
            animationKey={animationKey}
          />
          <PositionMetricCard
            label="Win Percentage"
            value={loading ? 'Loading' : <CountUpNumber value={stats.winPercent} formatter={formatHome1Percent} />}
            caption="On table sessions won"
            tone="positive"
            animationKey={animationKey}
          />
        </div>

        <div className="home1-tablegames-layout">
          <div className="home1-tablegames-board home1-tablegames-board--calendar" ref={tableCalendarRef}>
            <div className="home1-sidebets-board-header">
              <span>Table Game Heatmap</span>
            </div>

            {error ? <p className="home1-error">Unable to load table-game data.</p> : null}
            <div className="home1-tablegames-calendar-topline">
              <div className="home1-tablegames-calendar-legend" aria-label="Table game calendar legend">
                <span>
                  <i className="win" /> Win
                </span>
                <span>
                  <i className="lose" /> Loss
                </span>
                <span>
                  <i className="no-play" /> No action
                </span>
              </div>
              <strong>{loading ? 'Loading' : `${tableResultDays} played days`}</strong>
            </div>

            <div className="home1-tablegames-calendar-layout">
              <div className="home1-tablegames-calendar-months">
                {tableCalendar.months.map((month, monthIndex) => {
                  const hasSelectedDay = month.days.some((day) => day.key === selectedTableDay?.key);

                  return (
                    <article
                      className={`home1-tablegames-calendar-month${hasSelectedDay ? ' is-active' : ''}`}
                      key={month.name}
                      style={{ '--calendar-index': monthIndex }}
                    >
                      <h3>{month.name}</h3>
                      <div className="home1-tablegames-calendar-days">
                        {month.days.map((day) => {
                          const isSelected = selectedTableDay?.key === day.key;
                          const tooltipId = `home1-tablegames-day-tooltip-${day.key}`;

                          return (
                            <span
                              className={`home1-tablegames-calendar-day-wrap${isSelected ? ' is-active' : ''}`}
                              data-tooltip-align={monthIndex % 3 === 0 ? 'start' : monthIndex % 3 === 2 ? 'end' : 'center'}
                              key={day.key}
                            >
                              <button
                                type="button"
                                className={`home1-tablegames-calendar-day ${day.status}${isSelected ? ' active' : ''}`}
                                title={formatTableCalendarDayLabel(day)}
                                aria-describedby={isSelected ? tooltipId : undefined}
                                aria-label={formatTableCalendarDayLabel(day)}
                                aria-pressed={isSelected}
                                onClick={() => setSelectedTableDayKey((currentKey) => (currentKey === day.key ? null : day.key))}
                                style={{
                                  '--table-day-bg': day.heatColor,
                                  '--table-day-strength': day.heatIntensity,
                                }}
                              />

                              {isSelected ? (
                                <div className={`home1-tablegames-day-detail ${selectedTableDayTone}`} id={tooltipId} role="tooltip">
                                  <div className="home1-tablegames-day-detail-header">
                                    <span>Selected day</span>
                                    <strong>{formatShortDate(selectedTableDay.date)}</strong>
                                  </div>

                                  <div className="home1-tablegames-day-games">
                                    <span>Table games played</span>
                                    {selectedTableDay.games?.length ? (
                                      <div>
                                        {selectedTableDay.games.map((game) => (
                                          <em key={`${selectedTableDay.key}-${game}`}>{game}</em>
                                        ))}
                                      </div>
                                    ) : (
                                      <p>{selectedTableDayTone === 'pending' ? 'Pending table-game data.' : 'No table games logged.'}</p>
                                    )}
                                  </div>

                                  <div className="home1-tablegames-day-stats">
                                    <article>
                                      <span>Result</span>
                                      <strong>{getTableDayResultLabel(selectedTableDay)}</strong>
                                    </article>
                                    <article>
                                      <span>Running table position</span>
                                      <strong>
                                        {selectedTableDay.cumulative == null ? '—' : <CountUpCurrency value={selectedTableDay.cumulative} />}
                                      </strong>
                                    </article>
                                  </div>
                                </div>
                              ) : null}
                            </span>
                          );
                        })}
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="home1-tablegames-board home1-tablegames-board--mix">
            <div className="home1-sidebets-board-header">
              <span>Game mix</span>
              <strong>{loading ? 'Loading' : `${gameCounts.length} games`}</strong>
            </div>

            <div className="home1-tablegames-mix">
              {gameCounts.map((game, index) => (
                <article
                  className="home1-tablegames-mix-row"
                  key={game.label}
                  style={{
                    '--table-index': index,
                    '--table-bar-size': `${Math.max(8, (game.count / maxGameCount) * 100)}%`,
                    '--table-heat-color': getHome1HeatRampColor(game.count, 0, maxGameCount),
                  }}
                >
                  <div>
                    <span>{game.label}</span>
                    <strong>
                      <CountUpNumber value={game.count} /> sessions
                    </strong>
                  </div>
                  <i aria-hidden="true" />
                </article>
              ))}
            </div>

          </div>
        </div>
      </div>
    </RevealOnScroll>
  );
}

function Home1CommunityBarList({ rows, loading, emptyMessage, ariaLabel }) {
  const maxValue = rows.length ? Math.max(...rows.map((row) => row.value), 1) : 1;
  const displayRows =
    loading && !rows.length
      ? [
          { key: 'loading-1', label: 'Loading community data', value: 0 },
          { key: 'loading-2', label: 'Reading social board', value: 0 },
          { key: 'loading-3', label: 'Counting moments', value: 0 },
        ]
      : rows;

  if (!loading && !displayRows.length) {
    return <p className="home1-sidebets-empty">{emptyMessage}</p>;
  }

  const rankMax = Math.max(displayRows.length - 1, 1);

  return (
    <div className="home1-community-bars" aria-label={ariaLabel}>
      {displayRows.map((row, index) => (
        <article
          className="home1-community-bar-row"
          key={row.key || row.label}
          style={{
            '--community-index': index,
            '--community-bar-size': `${Math.max(4, (row.value / maxValue) * 100)}%`,
            '--community-heat-color': getHome1HeatRampColor(rankMax - index, 0, rankMax),
          }}
        >
          <span className="home1-sidebets-rank">{String(index + 1).padStart(2, '0')}</span>
          <span className="home1-community-bar-label">{row.label}</span>
          <strong>{loading && !rows.length ? 'Loading' : <CountUpNumber value={row.value} />}</strong>
          <i aria-hidden="true">
            <b />
          </i>
        </article>
      ))}
    </div>
  );
}

function Home1CommunitySection({
  loading,
  error,
  summary,
  sayings,
  trends,
  gruntStats,
  animationKey,
}) {
  const gruntSuccessAngle = `${Math.max(0, Math.min(100, gruntStats.successPct)) * 3.6}deg`;

  return (
    <RevealOnScroll className="home1-community-section" id="home1-community">
      <div className="home1-community-shell">
        <div className="home1-community-header">
          <div>
            <p className="home1-eyebrow">Community & Fun</p>
            <h2>Community Stats</h2>
            <p>Sayings, trends, and EJ grunts!</p>
          </div>
        </div>

        <div className="home1-position-stats home1-community-stats">
          <PositionMetricCard
            label="Big Check Amount"
            value={loading ? 'Loading' : <CountUpCurrency value={summary.bigCheckAmount} />}
            caption="Total big-check dollars logged"
            tone="positive"
            animationKey={animationKey}
          />
          <PositionMetricCard
            label="Big Checks"
            value={loading ? 'Loading' : <CountUpNumber value={summary.bigChecks} />}
            caption="Big check count"
            tone="positive"
            animationKey={animationKey}
          />
          <PositionMetricCard
            label="Triple Pops"
            value={loading ? 'Loading' : <CountUpNumber value={summary.triplePops} />}
            caption="Triple-pop moments tracked"
            tone="positive"
            animationKey={animationKey}
          />
          <PositionMetricCard
            label="One Ticketers"
            value={loading ? 'Loading' : <CountUpNumber value={summary.oneTicketersSlot} />}
            caption="Slot one-ticketer count"
            tone="neutral"
            animationKey={animationKey}
          />
        </div>

        <div className="home1-community-layout">
          <div className="home1-community-board home1-community-board--sayings">
            <div className="home1-sidebets-board-header">
              <span>Social sayings</span>
            </div>
            {error ? <p className="home1-error">Unable to load community stats.</p> : null}
            <Home1CommunityBarList
              rows={sayings}
              loading={loading}
              emptyMessage="No social sayings available yet."
              ariaLabel="Top social sayings"
            />
          </div>

          <div className="home1-community-board home1-community-board--grunts">
            <div className="home1-sidebets-board-header">
              <span>EJ grunts</span>
              <strong>{loading ? 'Loading' : `${formatInteger(gruntStats.total)} total`}</strong>
            </div>
            <div className="home1-community-grunts-layout">
              <div
                className="home1-community-grunt-dial"
                style={{ '--grunt-success-angle': gruntSuccessAngle }}
                aria-label={`EJ grunt success percentage ${formatHome1Percent(gruntStats.successPct)}`}
              >
                <span>{loading ? '...' : <CountUpNumber value={gruntStats.successPct} formatter={formatHome1Percent} />}</span>
                <small>success</small>
              </div>
              <div className="home1-community-grunt-rows">
                <article className="positive">
                  <span>Successful</span>
                  <strong>{loading ? 'Loading' : <CountUpNumber value={gruntStats.successful} />}</strong>
                </article>
                <article className="negative">
                  <span>Unsuccessful</span>
                  <strong>{loading ? 'Loading' : <CountUpNumber value={gruntStats.unsuccessful} />}</strong>
                </article>
                <article>
                  <span>Total grunts</span>
                  <strong>{loading ? 'Loading' : <CountUpNumber value={gruntStats.total} />}</strong>
                </article>
              </div>
            </div>
          </div>

          <div className="home1-community-board home1-community-board--trends">
            <div className="home1-sidebets-board-header">
              <span>Trends</span>
            </div>
            <Home1CommunityBarList
              rows={trends}
              loading={loading}
              emptyMessage="No trend data available yet."
              ariaLabel="Top community trends"
            />
          </div>
        </div>
      </div>
    </RevealOnScroll>
  );
}

function SplitText({ text }) {
  return (
    <span className="home1-split" aria-label={text}>
      {text.split(' ').map((word, index) => (
        <span key={`${word}-${index}`} style={{ '--split-delay': `${index * 55}ms` }} aria-hidden="true">
          {word}
        </span>
      ))}
    </span>
  );
}

function useInViewOnce({ threshold = 0.16, rootMargin = '0px 0px -8% 0px' } = {}) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element || isVisible) return undefined;

    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion) {
      setIsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold, rootMargin },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [isVisible, rootMargin, threshold]);

  return [ref, isVisible];
}

function ReactBitsAnimatedContent({
  as: Component = 'div',
  children,
  className = '',
  delay = 0,
  distance = 28,
  direction = 'vertical',
  reverse = false,
  threshold = 0.16,
  rootMargin = '0px 0px -8% 0px',
  style,
  ...props
}) {
  const [ref, isVisible] = useInViewOnce({ threshold, rootMargin });

  return (
    <Component
      ref={ref}
      className={`home1-rb-content${isVisible ? ' is-visible' : ''}${className ? ` ${className}` : ''}`}
      data-rb-direction={direction}
      data-rb-reverse={reverse ? 'true' : 'false'}
      style={{
        '--rb-delay': `${delay}ms`,
        '--rb-distance': `${distance}px`,
        ...style,
      }}
      {...props}
    >
      {children}
    </Component>
  );
}

function useCountUpValue(value, duration = 1200, isActive = true) {
  const [displayValue, setDisplayValue] = useState(0);
  const displayValueRef = useRef(0);

  useEffect(() => {
    if (!isActive || isMissingNumber(value)) return undefined;

    let frameId;
    const startTime = performance.now();
    const startValue = displayValueRef.current;
    const endValue = Number(value);
    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion) {
      displayValueRef.current = endValue;
      setDisplayValue(endValue);
      return undefined;
    }

    const tick = (now) => {
      const progress = Math.min(1, (now - startTime) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      const nextValue = startValue + (endValue - startValue) * eased;
      displayValueRef.current = nextValue;
      setDisplayValue(nextValue);
      if (progress < 1) {
        frameId = requestAnimationFrame(tick);
      } else {
        displayValueRef.current = endValue;
        setDisplayValue(endValue);
      }
    };

    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [duration, isActive, value]);

  return displayValue;
}

function CountUpCurrency({ value, compact = false }) {
  const [ref, isVisible] = useInViewOnce({ threshold: 0.2, rootMargin: '0px 0px -6% 0px' });
  const displayValue = useCountUpValue(value, 1200, isVisible);
  const formattedValue = isMissingNumber(value)
    ? formatCurrency(value)
    : compact
    ? formatCompactCurrency(displayValue)
    : formatCurrency(displayValue);

  return (
    <span ref={ref} className="home1-count-up">
      {formattedValue}
    </span>
  );
}

function CountUpNumber({ value, formatter = formatInteger }) {
  const [ref, isVisible] = useInViewOnce({ threshold: 0.2, rootMargin: '0px 0px -6% 0px' });
  const displayValue = useCountUpValue(value, 1200, isVisible);
  const formattedValue = isMissingNumber(value) ? formatter(value) : formatter(displayValue);

  return (
    <span ref={ref} className="home1-count-up">
      {formattedValue}
    </span>
  );
}

function CountUpDayCount({ value }) {
  return <CountUpNumber value={value} formatter={formatDayCount} />;
}

function AnimatedStreakPie({ loading, totalDays, winSlice, lossSlice, animationKey }) {
  const [ref, isVisible] = useInViewOnce({ threshold: 0.28, rootMargin: '0px 0px -8% 0px' });
  const [drawAngle, setDrawAngle] = useState(0);

  useEffect(() => {
    if (!isVisible || loading) {
      setDrawAngle(0);
      return undefined;
    }

    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion) {
      setDrawAngle(360);
      return undefined;
    }

    let frameId;
    const duration = 1100;
    const startTime = performance.now();

    setDrawAngle(0);

    const tick = (now) => {
      const progress = Math.min(1, (now - startTime) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDrawAngle(360 * eased);

      if (progress < 1) {
        frameId = requestAnimationFrame(tick);
      } else {
        setDrawAngle(360);
      }
    };

    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [animationKey, isVisible, loading, lossSlice, totalDays, winSlice]);

  return (
    <div
      ref={ref}
      className="home1-streak-pie"
      style={{
        '--win-slice': `${winSlice}deg`,
        '--loss-slice': `${winSlice + lossSlice}deg`,
        '--draw-slice': `${drawAngle}deg`,
      }}
      aria-hidden="true"
    >
      <span>{loading ? '...' : <CountUpNumber value={totalDays} />}</span>
      <small>tracked days</small>
    </div>
  );
}

function ActiveStreakValue({ direction, days }) {
  if (!days) return 'No active streak';
  return (
    <>
      {direction} <CountUpDayCount value={days} />
    </>
  );
}

function Home1TickerValue({ fact }) {
  if (fact.valueLabel) return fact.valueLabel;
  if (fact.type === 'currency') return <CountUpCurrency value={fact.value} compact={fact.compact} />;
  return <CountUpNumber value={fact.value} formatter={fact.formatter} />;
}

function Home1Ticker({ facts, onSelect }) {
  const tickerFacts = facts.length ? facts : HOME1_TICKER_LOADING_FACTS;
  const [ref, isVisible] = useInViewOnce({ threshold: 0.12, rootMargin: '0px 0px -4% 0px' });
  const [tickerLayout, setTickerLayout] = useState({
    cardWidth: 180,
    gap: 16,
    distance: tickerFacts.length * 196,
    duration: 72,
  });

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    const clamp = (min, value, max) => Math.min(max, Math.max(min, value));

    const updateLayout = () => {
      const width = element.getBoundingClientRect().width || window.innerWidth || 1200;
      const cardWidth = 180;
      const gap = clamp(14, width * 0.014, 22);
      const stride = cardWidth + gap;

      setTickerLayout({
        cardWidth,
        gap,
        distance: tickerFacts.length * stride,
        duration: Math.max(72, Math.round((tickerFacts.length * stride) / 30)),
      });
    };

    updateLayout();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateLayout);
      return () => window.removeEventListener('resize', updateLayout);
    }

    const observer = new ResizeObserver(updateLayout);
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, tickerFacts.length]);

  const tickerItems = [...tickerFacts, ...tickerFacts];
  const tickerStride = tickerLayout.cardWidth + tickerLayout.gap;

  return (
    <div
      ref={ref}
      className={`home1-ticker${isVisible ? ' is-visible' : ''}`}
      aria-label="Live Vegas Matt stat facts"
      style={{
        '--ticker-card-gap': `${tickerLayout.gap}px`,
        '--ticker-card-width': `${tickerLayout.cardWidth}px`,
        '--ticker-distance': `${tickerLayout.distance}px`,
        '--ticker-duration': `${tickerLayout.duration}s`,
        '--ticker-track-width': `${tickerLayout.distance * 2}px`,
      }}
    >
      <div
        className="home1-ticker-track"
        style={{ '--ticker-distance': `${tickerLayout.distance}px` }}
      >
        {tickerItems.map((fact, index) => {
          const isDuplicate = index >= tickerFacts.length;
          const targetId = fact.targetId;

          return (
            <button
              aria-hidden={isDuplicate}
              aria-label={`Go to ${fact.label}`}
              className={`home1-ticker-item ${fact.tone || 'neutral'}`}
              key={`${index}-${fact.key}`}
              onClick={() => {
                if (targetId) onSelect?.(targetId);
              }}
              style={{ '--ticker-card-x': `${index * tickerStride}px` }}
              tabIndex={isDuplicate ? -1 : 0}
              title={targetId ? `Go to ${fact.label}` : undefined}
              type="button"
            >
              <span className="home1-ticker-label">{fact.label}</span>
              <strong className="home1-ticker-value">
                <Home1TickerValue fact={fact} />
              </strong>
              <small>{fact.note}</small>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function RevealOnScroll({ children, className = '', id }) {
  return (
    <ReactBitsAnimatedContent id={id} className={`home1-reveal ${className}`} distance={34} threshold={0.14}>
      {children}
    </ReactBitsAnimatedContent>
  );
}

function PositionMetricCard({ label, value, caption, tone = 'neutral', animationKey }) {
  return (
    <ReactBitsAnimatedContent
      as="article"
      className={`home1-position-metric ${tone}`}
      data-animation-key={animationKey}
      distance={22}
      threshold={0.18}
    >
      <span>{label}</span>
      <strong>{value}</strong>
      {caption ? <small>{caption}</small> : null}
    </ReactBitsAnimatedContent>
  );
}

function Home1YearDock({ filters, activeKey, visible, onSelect }) {
  return (
    <div className={`home1-year-dock${visible ? ' is-visible' : ''}`} aria-hidden={!visible}>
      <div className="home1-year-dock__rail" role="group" aria-label="Home1 data range selector">
        {filters.map((filter) => (
          <button
            key={filter.key}
            type="button"
            className={`home1-year-dock__item${activeKey === filter.key ? ' active' : ''}`}
            onClick={() => onSelect(filter.key)}
            aria-pressed={activeKey === filter.key}
            tabIndex={visible ? 0 : -1}
          >
            <span>{filter.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function Home1FooterIcon({ path, viewBox = '0 0 512 512' }) {
  return (
    <div className="agency__icon w-embed">
      <svg width="100%" height="100%" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox={viewBox} role="presentation">
        <path d={path} />
      </svg>
    </div>
  );
}

function Home1Footer() {
  const renderFooterLink = (link) => {
    const className = ['nav__list__link', link.current ? 'w--current' : '', link.empty ? 'w-dyn-bind-empty' : '']
      .filter(Boolean)
      .join(' ');

    return (
      <a
        id={link.id}
        key={link.id}
        href={link.href}
        aria-current={link.current ? 'page' : undefined}
        className={className}
        target={link.newTab ? '_blank' : undefined}
        aria-label={link.newTab ? 'Opens in a new tab' : undefined}
      >
        {link.label}
      </a>
    );
  };

  const renderColumnLinks = (column) => {
    if (!column.dynamic) return column.links.map((link) => renderFooterLink(link));

    return (
      <div className="w-dyn-list">
        <div role="list" className="w-dyn-items">
          {column.links.map((link) => (
            <div role="listitem" className="w-dyn-item" key={link.id}>
              {renderFooterLink(link)}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <footer id="Footer" className="footer">
      <div className="motto">
        <a id="Footer-Partners-Button" href={`${VEGAS_MATT_SITE_URL}/partners`} className="button button--footer">
          Partner With Vegas Matt!
        </a>
        <a id="Motto-Logo" href={VEGAS_MATT_SITE_URL} className="motto__brand w-inline-block">
          <div className="wordmark wordmark__text--motto">
            Vegas Matt<span className="tm tm--logo">&reg;</span>
          </div>
          <div className="motto__text">Let&apos;s Enjoy This!</div>
        </a>
        <a
          id="Footer-Subscribe-Button"
          href={VEGAS_MATT_SUBSCRIBE_URL}
          target="_blank"
          className="button button--footer"
          aria-label="Opens in a new tab"
        >
          Subscribe to Our Email List!
        </a>
      </div>

      <nav aria-label="sitemap" className="nav__list">
        {HOME1_FOOTER_COLUMNS.map((column) => (
          <div className={`nav__list__col${column.title === 'FInd Us' ? ' nav__list__col--2' : ''}`} key={column.title}>
            <div className="nav__list__h2">{column.title}</div>
            {renderColumnLinks(column)}
          </div>
        ))}
      </nav>

      <div className="agency">
        <div className="agency__link">
          &ldquo;Vegas Matt&rdquo; &reg; is a registered trademark of Matt Morrow Enterprises, LLC under U.S. Trademark
          Registration No. 7421370. Unauthorized use, reproduction, or distribution of this trademark or related
          intellectual property, including but not limited to designs, brand elements, merchandise, videos, images, text,
          and any other content is strictly prohibited and will be prosecuted to the fullest extent of the law.
          <br />
          <br />
          All rights reserved in the United States and other jurisdictions.
          <br />
          <br />
          For licensing or permissions, contact legal@vegasmatt.com
        </div>
        <div id="Agency-Copyright-Link" className="agency__link agency__link--copyright">
          <Home1FooterIcon path={FOOTER_COPYRIGHT_ICON_PATH} />
          <div id="Agency-Copyright-Year" className="agency__text">
            {CURRENT_YEAR}
          </div>
          <div className="agency__text">&nbsp;Matt&nbsp;Morrow Enterprises, LLC. All rights reserved.</div>
        </div>
        <div id="Agency-Site-Credit" className="agency__link agency__link--copyright">
          <div className="agency__text">
            Designed and maintained by <a href="mailto:info@jonathonaliss.co.uk">Jon Aliss</a>, stats by the Discord stats team.
          </div>
        </div>
      </div>
    </footer>
  );
}

function Home1Page() {
  const { mainSeries, theoreticalSeries, loading, error } = useOverviewData(true);
  const bonuses = useBonusesData();
  const [home1RangeFilter, setHome1RangeFilter] = useState(`${DEFAULT_POSITION_YEAR}`);
  const [isYearDockVisible, setIsYearDockVisible] = useState(false);
  const [isSiteMenuOpen, setIsSiteMenuOpen] = useState(false);

  useEffect(() => {
    document.body.classList.add('home1-body');
    return () => document.body.classList.remove('home1-body');
  }, []);

  useEffect(() => {
    let frameId;
    const updateDockVisibility = () => {
      frameId = undefined;
      const threshold = Math.min(420, (window.innerHeight || 900) * 0.48);
      setIsYearDockVisible(window.scrollY > threshold);
    };
    const requestUpdate = () => {
      if (frameId) return;
      frameId = requestAnimationFrame(updateDockVisibility);
    };

    updateDockVisibility();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    return () => {
      if (frameId) cancelAnimationFrame(frameId);
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
    };
  }, []);

  useEffect(() => {
    if (!isSiteMenuOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsSiteMenuOpen(false);
      }
    };

    const handleResize = () => {
      if (window.innerWidth > 991) {
        setIsSiteMenuOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleResize);
    };
  }, [isSiteMenuOpen]);

  const home1SelectedYear = home1RangeFilter === 'all' ? null : Number(home1RangeFilter);
  const home1RangeLabel = home1SelectedYear == null ? 'All time' : `${home1SelectedYear}`;
  const positionSelectedYear = home1SelectedYear;
  const activeMainSeries = useMemo(() => {
    if (positionSelectedYear == null) return mainSeries;
    return mainSeries.filter((entry) => entry.date.getFullYear() === positionSelectedYear);
  }, [mainSeries, positionSelectedYear]);
  const activeTheoreticalSeries = useMemo(() => {
    if (positionSelectedYear == null) return theoreticalSeries;
    return theoreticalSeries.filter((entry) => entry.date.getFullYear() === positionSelectedYear);
  }, [positionSelectedYear, theoreticalSeries]);
  const positionLabel = home1RangeLabel;
  const positionToDateLabel = `${positionLabel} to date`;
  const highestBalanceLabel = `Highest balance, ${positionToDateLabel.toLowerCase()}`;
  const currentStatusCaption = home1SelectedYear == null ? 'All time' : 'On the year';

  const stats = useMemo(
    () => buildHomeStats(activeMainSeries, activeTheoreticalSeries),
    [activeMainSeries, activeTheoreticalSeries],
  );
  const streakSelectedYear = home1SelectedYear;
  const streakMainSeries = useMemo(() => {
    if (streakSelectedYear == null) return mainSeries;
    return mainSeries.filter((entry) => entry.date.getFullYear() === streakSelectedYear);
  }, [mainSeries, streakSelectedYear]);
  const streakStats = useMemo(() => buildHomeStats(streakMainSeries, []), [streakMainSeries]);
  const streakLabel = home1RangeLabel;
  const currentPositionTone = getToneFromValue(stats.currentPosition);
  const highWaterMarkTone = getToneFromValue(stats.highWaterMark);
  const theoreticalHighWaterMarkTone = getToneFromValue(stats.theoreticalHighWaterMark);
  const activeStreakTone =
    streakStats.currentWinningStreak > 0 ? 'positive' : streakStats.currentLosingStreak > 0 ? 'negative' : 'neutral';
  const activeStreakDirection =
    streakStats.currentWinningStreak > 0 ? 'Winning' : streakStats.currentLosingStreak > 0 ? 'Losing' : 'Even';
  const activeStreakDays =
    streakStats.currentWinningStreak > 0
      ? streakStats.currentWinningStreak
      : streakStats.currentLosingStreak > 0
      ? streakStats.currentLosingStreak
      : 0;
  const activeStreakValue =
    streakStats.currentWinningStreak > 0 || streakStats.currentLosingStreak > 0
      ? <ActiveStreakValue direction={activeStreakDirection} days={activeStreakDays} />
      : 'No active streak';
  const activeStreakCaption = activeStreakDays ? 'In a row' : null;
  const worstRunCaption = `Worst run of ${streakLabel.toLowerCase()}`;
  const streakTotalDays = Math.max(0, streakStats.winningDays + streakStats.losingDays + streakStats.evenDays);
  const streakPieRows = [
    { label: 'Winning days', value: streakStats.winningDays, tone: 'positive' },
    { label: 'Losing days', value: streakStats.losingDays, tone: 'negative' },
    { label: 'Break even days', value: streakStats.evenDays, tone: 'neutral' },
  ];
  const streakExtremeRows = [
    { label: 'Biggest Winning Day', point: streakStats.biggestWinDay, tone: 'positive' },
    { label: 'Biggest Losing Day', point: streakStats.biggestLossDay, tone: 'negative' },
  ];
  const winSlice = streakTotalDays ? (streakStats.winningDays / streakTotalDays) * 360 : 0;
  const lossSlice = streakTotalDays ? (streakStats.losingDays / streakTotalDays) * 360 : 0;
  const calendarYear = home1SelectedYear ?? DEFAULT_POSITION_YEAR;
  const sideBetsRangeLabel =
    home1SelectedYear == null
      ? 'All time'
      : home1SelectedYear === CURRENT_YEAR
      ? `${home1SelectedYear} so far`
      : `${home1SelectedYear} total`;
  const sideBets = useHome1SideBets(home1SelectedYear);
  const home1SideBetSummary = useMemo(() => buildHome1SideBetSummary(sideBets.players), [sideBets.players]);
  const community = useHome1Social(home1SelectedYear);
  const home1SocialSummary = useMemo(() => buildHome1SocialSummary(community.row), [community.row]);
  const home1SocialSayings = useMemo(
    () => buildHome1SocialRows(community.row, HOME1_SOCIAL_SAYINGS_MAP, 7),
    [community.row],
  );
  const home1SocialTrends = useMemo(
    () => buildHome1SocialRows(community.row, HOME1_SOCIAL_TRENDS_MAP, 6),
    [community.row],
  );
  const home1GruntStats = useMemo(() => buildHome1GruntStats(community.row), [community.row]);
  const home1BonusRows = useMemo(() => {
    if (home1SelectedYear == null) return bonuses.rows;
    return bonuses.rows.filter((row) => row.date.getFullYear() === home1SelectedYear);
  }, [bonuses.rows, home1SelectedYear]);
  const home1BonusStats = useMemo(() => buildHome1BonusStats(home1BonusRows), [home1BonusRows]);
  const home1TopBonuses = useMemo(() => buildHome1TopBonuses(home1BonusRows), [home1BonusRows]);
  const home1FavoriteSlots = useMemo(() => buildHome1CountRows(home1BonusRows, 'slots'), [home1BonusRows]);
  const home1TableSeries = useMemo(() => buildHome1TableSeries(home1BonusRows), [home1BonusRows]);
  const home1TableStats = useMemo(() => buildHome1TableStats(home1TableSeries), [home1TableSeries]);
  const home1GameCounts = useMemo(() => buildHome1CountRows(home1BonusRows, 'tableGames'), [home1BonusRows]);
  const tickerFacts = useMemo(() => {
    if (!mainSeries.length) return HOME1_TICKER_LOADING_FACTS;

    return shuffleTickerFacts(
      buildHomeTickerFacts({
        stats,
        streakStats,
        positionLabel,
        positionToDateLabel,
        streakLabel,
        streakTotalDays,
        activeStreakDays,
        activeStreakDirection,
        activeStreakTone,
        sideBetSummary: home1SideBetSummary,
        bonusStats: home1BonusStats,
        topBonuses: home1TopBonuses,
        favoriteSlots: home1FavoriteSlots,
        tableStats: home1TableStats,
        gameCounts: home1GameCounts,
        communitySummary: home1SocialSummary,
        socialSayings: home1SocialSayings,
        socialTrends: home1SocialTrends,
        gruntStats: home1GruntStats,
        communityLoaded: Boolean(community.row),
      }),
    );
  }, [
    activeStreakDays,
    activeStreakDirection,
    activeStreakTone,
    community.row,
    home1BonusStats,
    home1FavoriteSlots,
    home1GameCounts,
    home1GruntStats,
    home1SideBetSummary,
    home1SocialSayings,
    home1SocialSummary,
    home1SocialTrends,
    home1TableStats,
    home1TopBonuses,
    mainSeries.length,
    positionLabel,
    positionToDateLabel,
    stats,
    streakLabel,
    streakStats,
    streakTotalDays,
  ]);

  const scrollToHome1Target = (targetId) => {
    if (!targetId) return;
    window.requestAnimationFrame(() => {
      document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  return (
    <div className="home1-page">
      <Home1YearDock
        filters={HOME1_RANGE_FILTERS}
        activeKey={home1RangeFilter}
        visible={isYearDockVisible}
        onSelect={setHome1RangeFilter}
      />

      <header id="Header" className="header">
        <a href="#Main" className="nav__skip">
          Skip to Main
        </a>
        <div
          data-collapse="tiny"
          data-animation="default"
          data-duration="200"
          data-no-scroll="1"
          data-easing2="ease"
          data-easing="ease"
          role="banner"
          className="nav w-nav"
        >
          <div className="nav__container">
            <a id="Nav-Logo" href="https://www.vegasmatt.com/" className="wordmark wordmark--nav">
              Vegas Matt<span className="tm tm--logo">&reg;</span>
            </a>
            <a id="Nav-Username-Link" href="https://www.vegasmatt.com/links" className="nav__socials__link">
              @VegasMatt
            </a>
            {HOME1_SITE_NAV_LINKS.map((item) => {
              const opensInNewTab = item.id === 'Subscribe';
              return (
                // eslint-disable-next-line react/jsx-no-target-blank
                <a
                  id={`Nav-${item.id}-Link`}
                  key={item.id}
                  href={item.href}
                  target={opensInNewTab ? '_blank' : undefined}
                  className="nav__link"
                  aria-label={opensInNewTab ? 'Opens in a new tab' : undefined}
                >
                  {item.label}
                </a>
              );
            })}
            {/* eslint-disable-next-line react/jsx-no-target-blank */}
            <a
              id="Nav-CTA-Button"
              role="button"
              aria-label="Opens in a new tab"
              href="https://vegasmatt.shop/"
              target="_blank"
              className="button button--nav w-inline-block"
            >
              <div className="icon icon-logo w-embed" aria-hidden="true">
                <svg width="100%" height="100%" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 417" role="presentation">
                  <path d={VEGAS_MATT_LOGO_PATH} />
                </svg>
              </div>
              <div className="button__text scale1">Shop Merch</div>
            </a>
            <div
              role="button"
              tabIndex={0}
              className={`nav__button w-nav-button${isSiteMenuOpen ? ' w--open' : ''}`}
              style={{ WebkitUserSelect: 'text' }}
              aria-label="menu"
              aria-controls="w-nav-overlay-0"
              aria-haspopup="menu"
              aria-expanded={isSiteMenuOpen}
              onClick={() => setIsSiteMenuOpen((current) => !current)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                setIsSiteMenuOpen((current) => !current);
              }}
            >
              <div className="icon icon__menu w-embed" aria-hidden="true">
                <svg width="100%" height="100%" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 612 524.6" role="presentation">
                  <path d={VEGAS_MATT_MENU_PATH} />
                </svg>
              </div>
            </div>
            <nav
              role="navigation"
              id="Nav-Drawer"
              aria-label="Vegas Matt mobile menu"
              aria-hidden={!isSiteMenuOpen}
              className={`nav__drawer w-nav-menu${isSiteMenuOpen ? ' is-open' : ''}`}
              data-nav-menu-open={isSiteMenuOpen ? '' : undefined}
              style={
                isSiteMenuOpen
                  ? { transition: 'all, transform 200ms', transform: 'translateY(0px) translateX(0px)' }
                  : undefined
              }
            >
              {HOME1_SITE_NAV_LINKS.map((item) => {
                const opensInNewTab = item.id === 'Subscribe';
                return (
                  // eslint-disable-next-line react/jsx-no-target-blank
                  <a
                    id={`Drawer-${item.id}-Link`}
                    key={`mobile-${item.id}`}
                    href={item.href}
                    onClick={() => setIsSiteMenuOpen(false)}
                    tabIndex={isSiteMenuOpen ? 0 : -1}
                    target={opensInNewTab ? '_blank' : undefined}
                    className="drawer__link"
                    aria-label={opensInNewTab ? 'Opens in a new tab' : undefined}
                  >
                    {item.label}
                  </a>
                );
              })}
              {/* eslint-disable-next-line react/jsx-no-target-blank */}
              <a
                id="Drawer-Merch-Button"
                role="button"
                aria-label="Opens in a new tab"
                href="https://vegasmatt.shop/"
                target="_blank"
                tabIndex={isSiteMenuOpen ? 0 : -1}
                className="button button--drawer w-inline-block"
                onClick={() => setIsSiteMenuOpen(false)}
              >
                <div className="icon icon-logo w-embed" aria-hidden="true">
                  <svg width="100%" height="100%" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 417" role="presentation">
                    <path d={VEGAS_MATT_LOGO_PATH} />
                  </svg>
                </div>
                <div className="button__text">Shop Merch</div>
              </a>
            </nav>
          </div>
          <div className="nav__bg" aria-hidden="true" />
          <div className="w-nav-overlay" data-wf-ignore="" id="w-nav-overlay-0" style={{ display: 'none' }} />
        </div>
      </header>

      <main id="Main">
        <section
          className="home1-hero"
          id="home1-top"
          style={{ '--home1-hero-image': `url("${IMAGE_BASE}/images/hero.jpg")` }}
        >
          <div className="home1-hero-copy">
            <p className="home1-eyebrow">Official stats</p>
            <h1>
              <SplitText text="Vegas Matt Stats" />
            </h1>
            <p className="home1-lede">
              Track the run, streaks, side bets, bonuses, table games, and more, here. All stats are for the Long Form
              videos on the Vegas Matt YouTube channel.
            </p>
            <div className="home1-actions">
              <button type="button" onClick={() => scrollToHome1Target('home1-run')}>
                See the Stats
              </button>
              <button className="home1-action-secondary" type="button" onClick={() => scrollToHome1Target('home1-sidebets')}>
                Side Bets
              </button>
            </div>
          </div>
          <Home1Ticker facts={tickerFacts} onSelect={scrollToHome1Target} />
        </section>

        <RevealOnScroll className="home1-position-so-far" id="home1-run">
          <div className="home1-position-header">
            <div>
              <p className="home1-eyebrow">Position so far</p>
              <h2>{positionLabel}</h2>
            </div>
          </div>

          <div className="home1-position-stats" id="summaryStats">
            <PositionMetricCard
              label="Current Position"
              value={loading ? 'Loading' : <CountUpCurrency value={stats.currentPosition} />}
              caption={positionToDateLabel}
              tone={currentPositionTone}
              animationKey={home1RangeFilter}
            />
            <PositionMetricCard
              label="High Water Mark"
              value={loading ? 'Loading' : <CountUpCurrency value={stats.highWaterMark} />}
              caption={highestBalanceLabel}
              tone={highWaterMarkTone}
              animationKey={home1RangeFilter}
            />
            <PositionMetricCard
              label="Current Status"
              value={loading ? 'Loading' : stats.statusText}
              caption={currentStatusCaption}
              tone={stats.statusTone}
              animationKey={home1RangeFilter}
            />
            <PositionMetricCard
              label="Theoretical High Water Mark"
              value={loading ? 'Loading' : <CountUpCurrency value={stats.theoreticalHighWaterMark} />}
              caption="If they quit at the high water mark each day"
              tone={theoreticalHighWaterMarkTone}
              animationKey={home1RangeFilter}
            />
          </div>

          <div className="home1-position-graph">
            <div className="home1-position-graph-header">
              <div>
                <span>Current Position</span>
                <strong>{loading ? 'Loading' : <CountUpCurrency value={stats.currentPosition} />}</strong>
              </div>
              <div className="home1-position-legend" aria-label="Chart legend">
                <span>
                  <i className="current" /> Current Position
                </span>
                <span>
                  <i className="theoretical" /> Theoretical HWM
                </span>
                <span aria-label="Break-even line">
                  <i className="zero" />
                </span>
              </div>
            </div>
            {error ? (
              <p className="home1-error">Unable to load the live chart data.</p>
            ) : (
              <Home1PositionChart mainSeries={activeMainSeries} theoreticalSeries={activeTheoreticalSeries} />
            )}
          </div>
        </RevealOnScroll>

        <RevealOnScroll className="home1-streaks-section" id="home1-streaks">
          <div className="home1-position-header">
            <div>
              <p className="home1-eyebrow">Winning and losing streaks</p>
              <h2>Streak Tracker</h2>
            </div>
          </div>

          <div className="home1-position-stats home1-streak-stats">
            <PositionMetricCard
              label="Current Streak"
              value={loading ? 'Loading' : activeStreakValue}
              caption={activeStreakCaption}
              tone={activeStreakTone}
              animationKey={home1RangeFilter}
            />
            <PositionMetricCard
              label="Longest Winning Streak"
              value={loading ? 'Loading' : <CountUpDayCount value={streakStats.longestWinningStreak} />}
              caption="Best run in a row"
              tone="positive"
              animationKey={home1RangeFilter}
            />
            <PositionMetricCard
              label="Longest Losing Streak"
              value={loading ? 'Loading' : <CountUpDayCount value={streakStats.longestLosingStreak} />}
              caption={worstRunCaption}
              tone="negative"
              animationKey={home1RangeFilter}
            />
          </div>

          <div className="home1-streak-graph">
            <div className="home1-position-graph-header">
              <div>
                <span>Daily Results</span>
                <strong className="neutral">Win / Loss Split</strong>
              </div>
              <div className="home1-position-legend" aria-label="Streak legend">
                <span>
                  <i className="theoretical" /> Win
                </span>
                <span>
                  <i className="zero" /> Loss
                </span>
                <span>
                  <i className="neutral" /> Even
                </span>
              </div>
            </div>

            <div className="home1-streak-board">
              <div className="home1-streak-pie-panel">
                <AnimatedStreakPie
                  key={`streak-pie-${home1RangeFilter}`}
                  loading={loading}
                  totalDays={streakTotalDays}
                  winSlice={winSlice}
                  lossSlice={lossSlice}
                  animationKey={home1RangeFilter}
                />
              </div>
              <div className="home1-streak-pie-legend">
                {streakPieRows.map((row) => (
                  <div className={`home1-streak-pie-row ${row.tone}`} key={`${row.label}-${home1RangeFilter}`}>
                    <span>{row.label}</span>
                    <strong>{loading ? 'Loading' : <CountUpNumber value={row.value} />}</strong>
                    <small>{loading ? '...' : formatPercent(row.value, streakTotalDays)}</small>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="home1-position-stats home1-streak-extreme-stats">
            {streakExtremeRows.map((row) => (
              <PositionMetricCard
                key={`${row.label}-${home1RangeFilter}`}
                label={row.label}
                value={loading ? 'Loading' : row.point ? <CountUpCurrency value={row.point.change} /> : '—'}
                caption={loading ? 'Checking selected board' : row.point ? formatShortDate(row.point.date) : 'No tracked day'}
                tone={row.tone}
                animationKey={home1RangeFilter}
              />
            ))}
          </div>
        </RevealOnScroll>

        <Home1CalendarSection
          year={calendarYear}
          mainSeries={mainSeries}
          loading={loading}
          error={error}
        />

        <Home1SideBetsSection
          players={sideBets.players}
          loading={sideBets.loading}
          error={sideBets.error}
          rangeLabel={sideBetsRangeLabel}
        />

        <Home1BonusesSection
          loading={bonuses.loading}
          error={bonuses.error}
          stats={home1BonusStats}
          topBonuses={home1TopBonuses}
          favoriteSlots={home1FavoriteSlots}
          animationKey={home1RangeFilter}
        />

        <Home1TableGamesSection
          loading={bonuses.loading}
          error={bonuses.error}
          tableCalendarYear={calendarYear}
          stats={home1TableStats}
          gameCounts={home1GameCounts}
          series={home1TableSeries}
          animationKey={home1RangeFilter}
        />

        <Home1CommunitySection
          loading={community.loading}
          error={community.error}
          summary={home1SocialSummary}
          sayings={home1SocialSayings}
          trends={home1SocialTrends}
          gruntStats={home1GruntStats}
          animationKey={home1RangeFilter}
        />
      </main>
      <Home1Footer />
    </div>
  );
}

export default Home1Page;
